<template>
  <b-modal
    modal-class="enhanced-treatment-plan-modal"
    id="enhanced-treatment-plan"
    ref="EnhancedTreatmentPlan"
    size="xl"
    header-class="py-2"
    title="แผนการรักษาจากภาพถ่าย"
    no-close-on-backdrop
    no-close-on-esc
    scrollable
    :hide-footer="currentStep < 2"
    @shown="enableNavigationLock"
    @hidden="handleModalHidden"
  >
    <!-- Progress Steps - Compact Horizontal Stepper -->
    <div class="horizontal-stepper mb-3">
      <div class="stepper-container">
        <div
          v-for="(step, index) in steps"
          :key="step.number"
          class="stepper-item"
        >
          <!-- Step Circle + Label Inline -->
          <div class="stepper-step-inline">
            <div
              class="stepper-dot"
              :class="{
                'dot-completed': currentStep > step.number,
                'dot-active': currentStep === step.number,
                'dot-pending': currentStep < step.number
              }"
            >
              <i v-if="currentStep > step.number" class="fas fa-check"></i>
              <span v-else>{{ step.number }}</span>
            </div>

            <div
              class="stepper-label"
              :class="{
                'label-active': currentStep === step.number,
                'label-completed': currentStep > step.number
              }"
            >
              <i :class="step.icon" class="mr-1"></i>
              <span>{{ step.title }}</span>
            </div>
          </div>

          <!-- Connecting Line (only between steps) -->
          <div
            v-if="index < steps.length - 1"
            class="stepper-line"
            :class="{ 'line-completed': currentStep > step.number }"
          ></div>
        </div>
      </div>
    </div>
    
    <!-- Protocol Upload Progress (during photo upload) -->
    <div v-if="isUploading" class="upload-progress-section mb-4">
      <b-alert show variant="info">
        <div class="d-flex align-items-center">
          <b-spinner small class="mr-2"></b-spinner>
          <div class="flex-grow-1">
            <strong>กำลังอัปโหลดภาพ...</strong>
            <div class="mt-1">
              <b-progress :value="uploadProgressPercentage" :max="100" height="20px" striped animated>
                <b-progress-bar :value="uploadProgressPercentage">
                  {{ uploadProgressPercentage }}%
                </b-progress-bar>
              </b-progress>
            </div>
          </div>
        </div>
      </b-alert>
    </div>
    
    <!-- Step 1: Enhanced Photo Input Options -->
    <div v-show="currentStep === 1" class="step-content">
      <h5 class="mb-3">
        <i class="fas fa-camera mr-2"></i>
        ขั้นตอนที่ 1: ถ่ายภาพ Occlusal View
      </h5>
      
      <!-- Enhanced Photo Input Interface -->
      <div class="photo-input-sections">
        <!-- Upper Photo Section -->
        <div class="photo-input-row mb-4">
          <h6 class="mb-3">
            <i class="fas fa-tooth mr-2"></i>
            ฟันบน (Upper Occlusal)
            <b-badge :variant="upperPhotoPreview ? 'success' : 'secondary'" class="ml-2">
              {{ upperPhotoPreview ? '✅ เสร็จแล้ว' : '❌ ยังไม่ได้เพิ่ม' }}
            </b-badge>
          </h6>
          
          <!-- Show captured photo if available -->
          <div v-if="upperPhotoPreview" class="captured-photo-display">
            <div
              ref="upperPhotoContainer"
              class="photo-container"
              :class="{ 'crop-mode-active': cropMode.active && cropMode.type === 'upper' }"
            >
              <b-img
                :src="upperPhotoPreview"
                fluid
                class="captured-photo-preview"
                alt="Upper occlusal photo"
              />

              <!-- Crop Overlay -->
              <div v-if="cropMode.active && cropMode.type === 'upper'" class="crop-overlay">
                <!-- Dark overlay outside crop area -->
                <div class="crop-overlay-bg"></div>

                <!-- Crop rectangle -->
                <div
                  class="crop-rectangle"
                  :style="getCropRectangleStyle()"
                  @mousedown="startDragCrop"
                  @touchstart="startDragCrop"
                >
                  <!-- Resize handles -->
                  <div class="crop-handle crop-handle-nw" @mousedown.stop="startResize('nw', $event)" @touchstart.stop="startResize('nw', $event)"></div>
                  <div class="crop-handle crop-handle-ne" @mousedown.stop="startResize('ne', $event)" @touchstart.stop="startResize('ne', $event)"></div>
                  <div class="crop-handle crop-handle-sw" @mousedown.stop="startResize('sw', $event)" @touchstart.stop="startResize('sw', $event)"></div>
                  <div class="crop-handle crop-handle-se" @mousedown.stop="startResize('se', $event)" @touchstart.stop="startResize('se', $event)"></div>

                  <!-- Edge handles -->
                  <div class="crop-handle crop-handle-n" @mousedown.stop="startResize('n', $event)" @touchstart.stop="startResize('n', $event)"></div>
                  <div class="crop-handle crop-handle-s" @mousedown.stop="startResize('s', $event)" @touchstart.stop="startResize('s', $event)"></div>
                  <div class="crop-handle crop-handle-e" @mousedown.stop="startResize('e', $event)" @touchstart.stop="startResize('e', $event)"></div>
                  <div class="crop-handle crop-handle-w" @mousedown.stop="startResize('w', $event)" @touchstart.stop="startResize('w', $event)"></div>

                  <!-- Grid lines -->
                  <div class="crop-grid">
                    <div class="crop-grid-line crop-grid-v" style="left: 33.33%"></div>
                    <div class="crop-grid-line crop-grid-v" style="left: 66.67%"></div>
                    <div class="crop-grid-line crop-grid-h" style="top: 33.33%"></div>
                    <div class="crop-grid-line crop-grid-h" style="top: 66.67%"></div>
                  </div>
                </div>
              </div>
            </div>
            <div class="photo-actions mt-2">
              <!-- Crop mode controls -->
              <div v-if="cropMode.active && cropMode.type === 'upper'" class="crop-controls mb-2">
                <b-button variant="success" size="sm" @click="applyCropToPhoto('upper')" class="mr-2">
                  <i class="fas fa-check"></i> ใช้ครอป
                </b-button>
                <b-button variant="secondary" size="sm" @click="cancelCrop">
                  <i class="fas fa-times"></i> ยกเลิก
                </b-button>
              </div>

              <!-- Regular transformation controls -->
              <div v-else>
                <b-button-group class="mr-2">
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="enterCropMode('upper')"
                    :disabled="isUpperPhotoExisting"
                    v-b-tooltip.hover
                    :title="isUpperPhotoExisting ? 'ไม่สามารถแก้ไขภาพเก่าได้ กรุณาถ่ายภาพใหม่' : 'ครอปภาพ'"
                  >
                    <i class="fas fa-crop"></i>
                  </b-button>
                  <b-button
                    variant="info"
                    size="sm"
                    @click="rotatePhoto('upper')"
                    :disabled="isUpperPhotoExisting"
                    v-b-tooltip.hover
                    :title="isUpperPhotoExisting ? 'ไม่สามารถแก้ไขภาพเก่าได้ กรุณาถ่ายภาพใหม่' : 'หมุน 90°'"
                  >
                    <i class="fas fa-rotate-right"></i>
                  </b-button>
                  <b-button
                    variant="info"
                    size="sm"
                    @click="flipPhotoHorizontal('upper')"
                    :disabled="isUpperPhotoExisting"
                    v-b-tooltip.hover
                    :title="isUpperPhotoExisting ? 'ไม่สามารถแก้ไขภาพเก่าได้ กรุณาถ่ายภาพใหม่' : 'พลิกซ้าย-ขวา'"
                  >
                    <i class="fas fa-arrows-alt-h"></i>
                  </b-button>
                  <b-button
                    variant="info"
                    size="sm"
                    @click="flipPhotoVertical('upper')"
                    :disabled="isUpperPhotoExisting"
                    v-b-tooltip.hover
                    :title="isUpperPhotoExisting ? 'ไม่สามารถแก้ไขภาพเก่าได้ กรุณาถ่ายภาพใหม่' : 'พลิกบน-ล่าง'"
                  >
                    <i class="fas fa-arrows-alt-v"></i>
                  </b-button>
                </b-button-group>
                <!-- Existing buttons -->
                <b-button variant="warning" size="sm" @click="replacePhoto('upper')" class="mr-2">
                  <i class="fas fa-redo"></i> เปลี่ยนภาพ
                </b-button>
                <b-button variant="danger" size="sm" @click="deletePhoto('upper')">
                  <i class="fas fa-trash"></i> ลบ
                </b-button>
              </div>
            </div>

            <!-- Quality Check Panel for Upper Photo -->
            <div v-if="upperPhotoQuality" class="quality-check-panel mt-3">
              <b-card :border-variant="upperPhotoQuality.quality === 'excellent' ? 'success' : upperPhotoQuality.quality === 'acceptable' ? 'warning' : 'danger'">
                <div class="d-flex align-items-center mb-2">
                  <i :class="getQualityIcon(upperPhotoQuality.score)" class="mr-2" :style="`color: ${upperPhotoQuality.quality === 'excellent' ? '#28a745' : upperPhotoQuality.quality === 'acceptable' ? '#ffc107' : '#dc3545'}`"></i>
                  <strong>{{ upperPhotoQuality.quality === 'excellent' ? 'คุณภาพดีเยี่ยม' : upperPhotoQuality.quality === 'acceptable' ? 'คุณภาพพอใช้' : 'คุณภาพต่ำ - ต้องถ่ายใหม่' }}</strong>
                </div>
                <small class="d-block text-muted mb-2">
                  {{ upperPhotoQuality.metrics.resolution.width }} x {{ upperPhotoQuality.metrics.resolution.height }} ({{ upperPhotoQuality.metrics.resolution.megapixels }} MP)
                </small>
                <ul class="quality-recommendations mb-0">
                  <li v-for="(rec, index) in upperPhotoQuality.recommendations" :key="index">
                    {{ rec }}
                  </li>
                </ul>
              </b-card>
            </div>

            <!-- Quality Check in Progress -->
            <div v-else-if="qualityCheckInProgress && upperPhotoPreview" class="text-center mt-3">
              <b-spinner small variant="primary" class="mr-2"></b-spinner>
              <small class="text-muted">กำลังตรวจสอบคุณภาพภาพ...</small>
            </div>
          </div>

          <!-- Single Unified Placeholder -->
          <div 
            v-else 
            class="unified-photo-placeholder"
            @click="showPhotoOptions('upper')"
            @dragover.prevent
            @dragenter.prevent="dragEnter('upper')"
            @dragleave.prevent="dragLeave('upper')"
            @drop.prevent="handleFileDrop($event, 'upper')"
            :class="{ 
              'drag-active': dragActive.upper,
              'hover-active': hoverActive.upper 
            }"
            @mouseenter="hoverActive.upper = true"
            @mouseleave="hoverActive.upper = false"
          >
            <div class="placeholder-content">
              <i class="fas fa-plus-circle fa-4x mb-3" :class="dragActive.upper ? 'text-success' : 'text-muted'"></i>
              <h5 class="mb-2" v-if="!dragActive.upper">คลิกเพื่อเพิ่มภาพฟันบน</h5>
              <h5 class="mb-2 text-success" v-else>วางไฟล์ที่นี่</h5>
              <p class="text-muted mb-0" v-if="!dragActive.upper">
                <i class="fas fa-camera mr-2"></i>ถ่ายภาพ 
                <span class="mx-2">|</span>
                <i class="fas fa-upload mr-2"></i>อัปโหลด
                <span class="mx-2">|</span>
                <i class="fas fa-hand-paper mr-2"></i>ลากวาง
              </p>
              <p class="text-success mb-0" v-else>
                รองรับ JPG, PNG, WebP (สูงสุด 10MB)
              </p>
            </div>
          </div>
        </div>

        <!-- Lower Photo Section -->
        <div class="photo-input-row mb-4">
          <h6 class="mb-3">
            <i class="fas fa-tooth mr-2"></i>
            ฟันล่าง (Lower Occlusal)
            <b-badge :variant="lowerPhotoPreview ? 'success' : 'secondary'" class="ml-2">
              {{ lowerPhotoPreview ? '✅ เสร็จแล้ว' : '❌ ยังไม่ได้เพิ่ม' }}
            </b-badge>
          </h6>
          
          <!-- Show captured photo if available -->
          <div v-if="lowerPhotoPreview" class="captured-photo-display">
            <div
              ref="lowerPhotoContainer"
              class="photo-container"
              :class="{ 'crop-mode-active': cropMode.active && cropMode.type === 'lower' }"
            >
              <b-img
                :src="lowerPhotoPreview"
                fluid
                class="captured-photo-preview"
                alt="Lower occlusal photo"
              />

              <!-- Crop Overlay -->
              <div v-if="cropMode.active && cropMode.type === 'lower'" class="crop-overlay">
                <!-- Dark overlay outside crop area -->
                <div class="crop-overlay-bg"></div>

                <!-- Crop rectangle -->
                <div
                  class="crop-rectangle"
                  :style="getCropRectangleStyle()"
                  @mousedown="startDragCrop"
                  @touchstart="startDragCrop"
                >
                  <!-- Resize handles -->
                  <div class="crop-handle crop-handle-nw" @mousedown.stop="startResize('nw', $event)" @touchstart.stop="startResize('nw', $event)"></div>
                  <div class="crop-handle crop-handle-ne" @mousedown.stop="startResize('ne', $event)" @touchstart.stop="startResize('ne', $event)"></div>
                  <div class="crop-handle crop-handle-sw" @mousedown.stop="startResize('sw', $event)" @touchstart.stop="startResize('sw', $event)"></div>
                  <div class="crop-handle crop-handle-se" @mousedown.stop="startResize('se', $event)" @touchstart.stop="startResize('se', $event)"></div>

                  <!-- Edge handles -->
                  <div class="crop-handle crop-handle-n" @mousedown.stop="startResize('n', $event)" @touchstart.stop="startResize('n', $event)"></div>
                  <div class="crop-handle crop-handle-s" @mousedown.stop="startResize('s', $event)" @touchstart.stop="startResize('s', $event)"></div>
                  <div class="crop-handle crop-handle-e" @mousedown.stop="startResize('e', $event)" @touchstart.stop="startResize('e', $event)"></div>
                  <div class="crop-handle crop-handle-w" @mousedown.stop="startResize('w', $event)" @touchstart.stop="startResize('w', $event)"></div>

                  <!-- Grid lines -->
                  <div class="crop-grid">
                    <div class="crop-grid-line crop-grid-v" style="left: 33.33%"></div>
                    <div class="crop-grid-line crop-grid-v" style="left: 66.67%"></div>
                    <div class="crop-grid-line crop-grid-h" style="top: 33.33%"></div>
                    <div class="crop-grid-line crop-grid-h" style="top: 66.67%"></div>
                  </div>
                </div>
              </div>
            </div>
            <div class="photo-actions mt-2">
              <!-- Crop mode controls -->
              <div v-if="cropMode.active && cropMode.type === 'lower'" class="crop-controls mb-2">
                <b-button variant="success" size="sm" @click="applyCropToPhoto('lower')" class="mr-2">
                  <i class="fas fa-check"></i> ใช้ครอป
                </b-button>
                <b-button variant="secondary" size="sm" @click="cancelCrop">
                  <i class="fas fa-times"></i> ยกเลิก
                </b-button>
              </div>

              <!-- Regular transformation controls -->
              <div v-else>
                <b-button-group class="mr-2">
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="enterCropMode('lower')"
                    :disabled="isLowerPhotoExisting"
                    v-b-tooltip.hover
                    :title="isLowerPhotoExisting ? 'ไม่สามารถแก้ไขภาพเก่าได้ กรุณาถ่ายภาพใหม่' : 'ครอปภาพ'"
                  >
                    <i class="fas fa-crop"></i>
                  </b-button>
                  <b-button
                    variant="info"
                    size="sm"
                    @click="rotatePhoto('lower')"
                    :disabled="isLowerPhotoExisting"
                    v-b-tooltip.hover
                    :title="isLowerPhotoExisting ? 'ไม่สามารถแก้ไขภาพเก่าได้ กรุณาถ่ายภาพใหม่' : 'หมุน 90°'"
                  >
                    <i class="fas fa-rotate-right"></i>
                  </b-button>
                  <b-button
                    variant="info"
                    size="sm"
                    @click="flipPhotoHorizontal('lower')"
                    :disabled="isLowerPhotoExisting"
                    v-b-tooltip.hover
                    :title="isLowerPhotoExisting ? 'ไม่สามารถแก้ไขภาพเก่าได้ กรุณาถ่ายภาพใหม่' : 'พลิกซ้าย-ขวา'"
                  >
                    <i class="fas fa-arrows-alt-h"></i>
                  </b-button>
                  <b-button
                    variant="info"
                    size="sm"
                    @click="flipPhotoVertical('lower')"
                    :disabled="isLowerPhotoExisting"
                    v-b-tooltip.hover
                    :title="isLowerPhotoExisting ? 'ไม่สามารถแก้ไขภาพเก่าได้ กรุณาถ่ายภาพใหม่' : 'พลิกบน-ล่าง'"
                  >
                    <i class="fas fa-arrows-alt-v"></i>
                  </b-button>
                </b-button-group>
                <!-- Existing buttons -->
                <b-button variant="warning" size="sm" @click="replacePhoto('lower')" class="mr-2">
                  <i class="fas fa-redo"></i> เปลี่ยนภาพ
                </b-button>
                <b-button variant="danger" size="sm" @click="deletePhoto('lower')">
                  <i class="fas fa-trash"></i> ลบ
                </b-button>
              </div>
            </div>

            <!-- Quality Check Panel for Lower Photo -->
            <div v-if="lowerPhotoQuality" class="quality-check-panel mt-3">
              <b-card :border-variant="lowerPhotoQuality.quality === 'excellent' ? 'success' : lowerPhotoQuality.quality === 'acceptable' ? 'warning' : 'danger'">
                <div class="d-flex align-items-center mb-2">
                  <i :class="getQualityIcon(lowerPhotoQuality.score)" class="mr-2" :style="`color: ${lowerPhotoQuality.quality === 'excellent' ? '#28a745' : lowerPhotoQuality.quality === 'acceptable' ? '#ffc107' : '#dc3545'}`"></i>
                  <strong>{{ lowerPhotoQuality.quality === 'excellent' ? 'คุณภาพดีเยี่ยม' : lowerPhotoQuality.quality === 'acceptable' ? 'คุณภาพพอใช้' : 'คุณภาพต่ำ - ต้องถ่ายใหม่' }}</strong>
                </div>
                <small class="d-block text-muted mb-2">
                  {{ lowerPhotoQuality.metrics.resolution.width }} x {{ lowerPhotoQuality.metrics.resolution.height }} ({{ lowerPhotoQuality.metrics.resolution.megapixels }} MP)
                </small>
                <ul class="quality-recommendations mb-0">
                  <li v-for="(rec, index) in lowerPhotoQuality.recommendations" :key="index">
                    {{ rec }}
                  </li>
                </ul>
              </b-card>
            </div>

            <!-- Quality Check in Progress -->
            <div v-else-if="qualityCheckInProgress && lowerPhotoPreview" class="text-center mt-3">
              <b-spinner small variant="primary" class="mr-2"></b-spinner>
              <small class="text-muted">กำลังตรวจสอบคุณภาพภาพ...</small>
            </div>
          </div>

          <!-- Single Unified Placeholder -->
          <div 
            v-else 
            class="unified-photo-placeholder"
            @click="showPhotoOptions('lower')"
            @dragover.prevent
            @dragenter.prevent="dragEnter('lower')"
            @dragleave.prevent="dragLeave('lower')"
            @drop.prevent="handleFileDrop($event, 'lower')"
            :class="{ 
              'drag-active': dragActive.lower,
              'hover-active': hoverActive.lower 
            }"
            @mouseenter="hoverActive.lower = true"
            @mouseleave="hoverActive.lower = false"
          >
            <div class="placeholder-content">
              <i class="fas fa-plus-circle fa-4x mb-3" :class="dragActive.lower ? 'text-success' : 'text-muted'"></i>
              <h5 class="mb-2" v-if="!dragActive.lower">คลิกเพื่อเพิ่มภาพฟันล่าง</h5>
              <h5 class="mb-2 text-success" v-else>วางไฟล์ที่นี่</h5>
              <p class="text-muted mb-0" v-if="!dragActive.lower">
                <i class="fas fa-camera mr-2"></i>ถ่ายภาพ 
                <span class="mx-2">|</span>
                <i class="fas fa-upload mr-2"></i>อัปโหลด
                <span class="mx-2">|</span>
                <i class="fas fa-hand-paper mr-2"></i>ลากวาง
              </p>
              <p class="text-success mb-0" v-else>
                รองรับ JPG, PNG, WebP (สูงสุด 10MB)
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Step Actions -->
      <div class="step-actions text-center">
        <b-button variant="secondary" @click="closeModal" class="mr-2">
          ยกเลิก
        </b-button>
        <b-button
          variant="primary"
          @click="proceedToAnalysis"
          :disabled="!hasNewPhotos || isProcessing"
          size="lg"
        >
          <b-spinner v-if="isProcessing" small class="mr-2"></b-spinner>
          {{ isProcessing ? 'กำลังอัปโหลด...' : 'บันทึกและถัดไป' }}
        </b-button>

        <!-- Skip button for old photos -->
        <b-button
          v-if="hasAllPhotos && !hasNewPhotos"
          variant="outline-primary"
          @click="skipToTreatmentPlan"
          size="lg"
          class="ml-2"
        >
          <i class="fas fa-forward mr-2"></i>
          ข้ามไปแผนการรักษา
        </b-button>

        <br>
        <small class="text-muted mt-2 d-block" v-if="!hasNewPhotos && !hasAllPhotos">
          กรุณาถ่ายภาพให้ครบทั้งฟันบนและฟันล่าง
        </small>
        <small class="text-muted mt-2 d-block" v-else-if="hasAllPhotos && !hasNewPhotos">
          กรุณาถ่ายภาพใหม่เพื่อวิเคราะห์ด้วย AI หรือข้ามไปแผนการรักษาโดยตรง
        </small>
      </div>
    </div>

    <!-- Simple Camera Modal -->
    <b-modal
      id="simple-camera-modal"
      ref="cameraModal"
      size="lg"
      :title="cameraModalTitle"
      no-close-on-backdrop
      no-close-on-esc
      hide-footer
    >
      <div class="simple-camera-container">
        <!-- Camera Preview -->
        <div v-if="!photoTakenInModal" class="camera-section">
          <div class="camera-preview-container">
            <video 
              ref="videoPreview" 
              class="camera-preview"
              autoplay
              playsinline
            ></video>
            
            <!-- Camera error -->
            <div v-if="cameraError" class="camera-error text-center p-4">
              <i class="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
              <p>{{ cameraError }}</p>
              <b-button variant="primary" @click="initCamera">ลองอีกครั้ง</b-button>
            </div>
          </div>
          
          <!-- Camera Controls -->
          <div class="camera-controls mt-3">
            <div class="text-center">
              <b-button
                variant="primary"
                size="lg"
                @click="capturePhotoInModal"
                :disabled="!cameraReady"
                class="mr-2"
              >
                <i class="fas fa-camera"></i> ถ่ายภาพ
              </b-button>

              <!-- Camera Switch Button -->
              <b-button
                v-if="availableCameras.length > 1"
                variant="outline-primary"
                size="lg"
                @click="switchCamera"
                :disabled="!canSwitchCamera"
                class="mr-2"
                v-b-tooltip.hover="currentFacingMode === 'environment' ? 'สลับเป็นกล้องหน้า' : 'สลับเป็นกล้องหลัง'"
              >
                <i
                  class="fas"
                  :class="switchingCamera ? 'fa-spinner fa-spin' : 'fa-sync-alt'"
                ></i>
                {{ switchingCamera ? 'กำลังสลับ...' : 'สลับกล้อง' }}
              </b-button>

              <b-button variant="secondary" @click="closeCamera">
                ยกเลิก
              </b-button>
            </div>
          </div>
        </div>

        <!-- Photo Review -->
        <div v-else class="photo-review-section">
          <div class="captured-photo-review text-center">
            <b-img 
              :src="capturedPhotoUrlInModal" 
              fluid 
              class="review-photo"
              alt="Captured photo"
            />
            
            <div class="review-actions mt-3">
              <b-button 
                variant="success" 
                size="lg" 
                @click="acceptPhoto"
                class="mr-2"
              >
                <i class="fas fa-check"></i> ใช้ภาพนี้
              </b-button>
              <b-button variant="secondary" @click="retakePhoto">
                <i class="fas fa-redo"></i> ถ่ายใหม่
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
    
    <!-- Step 2: Treatment Plan Review & Edit (formerly Step 3) -->
    <div v-show="currentStep === 2" class="step-content">
      <!-- Debug Information Panel (Development Only) -->
      <b-alert v-if="debugInfo && process.env.NODE_ENV === 'development'" show variant="info" class="mb-3">
        <h6><i class="fas fa-bug"></i> Database Integration Debug Info</h6>
        <div class="row small">
          <div class="col-md-4">
            <strong>Treatment Plan:</strong><br>
            Total: {{ debugInfo.treatmentPlanLength }}<br>
            AI Generated: {{ debugInfo.databaseStats.aiGenerated }}<br>
            Database-based: {{ debugInfo.databaseStats.databaseBased }}<br>
            Fallback: {{ debugInfo.databaseStats.fallback }}<br>
          </div>
          <div class="col-md-4">
            <strong>Quality Metrics:</strong><br>
            Avg Confidence: {{ debugInfo.databaseStats.averageConfidence }}%<br>
            High Confidence: {{ debugInfo.databaseStats.highConfidenceTreatments }}<br>
            Total Price: {{ debugInfo.databaseStats.totalPrice?.toLocaleString() }} ฿<br>
          </div>
          <div class="col-md-4">
            <strong>System Status:</strong><br>
            Analysis Complete: {{ debugInfo.analysisComplete ? '✓' : '✗' }}<br>
            Auto Plan Items: {{ debugInfo.autoGeneratedPlan }}<br>
          </div>
        </div>
        <details v-if="debugInfo.databaseStats.databaseCodes.length > 0" class="mt-2">
          <summary>Database Codes ({{ debugInfo.databaseStats.databaseCodes.length }})</summary>
          <code class="small">{{ debugInfo.databaseStats.databaseCodes.slice(0, 10).join(', ') }}</code>
        </details>
      </b-alert>
      
      <!-- Header with AI Status -->
      <div class="ai-status-header mb-3" v-if="analysisResults">
        <b-alert variant="info" show class="mb-3">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <i class="fas fa-robot mr-2"></i>
              <strong>AI Analysis Complete</strong> - พบปัญหา {{ analysisResults.problems ? analysisResults.problems.length : 0 }} จุด
            </div>
            <b-badge variant="success" pill>
              สร้างแผนการรักษา {{ treatmentPlan.length }} รายการ
            </b-badge>
          </div>
        </b-alert>
      </div>

      <!-- Photo Section (Independent of AI Analysis) -->
      <b-card v-if="upperPhotoPreview || lowerPhotoPreview" class="mb-3">
          <template #header>
            <h6 class="mb-0">
              <i class="fas fa-images mr-2"></i>
              ภาพถ่ายฟัน
              <span v-if="analysisResults && analysisResults.problems && analysisResults.problems.length > 0">
                - พบปัญหา {{ analysisResults.problems.length }} จุด
              </span>
            </h6>
          </template>
          
          <b-row>
            <!-- Upper Photo with Problems -->
            <b-col cols="6" v-if="upperPhotoPreview">
              <div class="photo-with-detections">
                <h6 class="text-center mb-2">ฟันบน</h6>
                <div class="detection-container">
                  <b-img
                    :src="upperPhotoPreview"
                    fluid
                    class="detection-base-image"
                    alt="Upper occlusal with detections"
                    @load="handleImageLoad('upper', $event)"
                    @error="handleImageError('upper', $event)"
                  />
                  <!-- Problem Overlays -->
                  <div 
                    v-for="(problem, index) in getProblemsForPhoto('upper')"
                    :key="`upper-problem-${index}`"
                    class="problem-overlay"
                    :style="getProblemOverlayStyle(problem)"
                    :title="`${problem.condition} (${Math.round(problem.confidence * 100)}%)`"
                  >
                    <div class="problem-label">
                      {{ problem.condition }}
                    </div>
                  </div>
                </div>
              </div>
            </b-col>
            
            <!-- Lower Photo with Problems -->
            <b-col cols="6" v-if="lowerPhotoPreview">
              <div class="photo-with-detections">
                <h6 class="text-center mb-2">ฟันล่าง</h6>
                <div class="detection-container">
                  <b-img
                    :src="lowerPhotoPreview"
                    fluid
                    class="detection-base-image"
                    alt="Lower occlusal with detections"
                    @load="handleImageLoad('lower', $event)"
                    @error="handleImageError('lower', $event)"
                  />
                  <!-- Problem Overlays -->
                  <div 
                    v-for="(problem, index) in getProblemsForPhoto('lower')"
                    :key="`lower-problem-${index}`"
                    class="problem-overlay"
                    :style="getProblemOverlayStyle(problem)"
                    :title="`${problem.condition} (${Math.round(problem.confidence * 100)}%)`"
                  >
                    <div class="problem-label">
                      {{ problem.condition }}
                    </div>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
          
          <!-- Problems Legend -->
          <div v-if="analysisResults && analysisResults.problems && analysisResults.problems.length > 0" class="problems-legend mt-3">
            <h6>ปัญหาที่พบ:</h6>
            <div class="problem-items">
              <b-badge
                v-for="(problem, index) in analysisResults.problems.slice(0, 8)"
                :key="`problem-${index}`"
                :variant="getProblemBadgeVariant(problem.confidence)"
                class="mr-2 mb-1"
              >
                {{ problem.condition }} ({{ Math.round(problem.confidence * 100) }}%)
              </b-badge>
              <b-badge
                v-if="analysisResults.problems.length > 8"
                variant="secondary"
                class="mr-2 mb-1"
              >
                +{{ analysisResults.problems.length - 8 }} อื่นๆ
              </b-badge>
            </div>
          </div>
        </b-card>

      <h5 class="mb-3">
        <i class="fas fa-edit mr-2"></i>
        ขั้นตอนที่ 2: ตรวจสอบและแก้ไขแผนการรักษา
      </h5>
      
      <!-- Action Toolbar -->
      <div class="action-toolbar mb-3 d-flex align-items-center">
        <b-button variant="primary" @click="openTreatmentModal" class="mr-2">
          <i class="fas fa-plus-circle mr-1"></i>
          เพิ่มการรักษา
        </b-button>
        <span class="text-muted ml-2">
          <small><i class="fas fa-info-circle mr-1"></i>คลิกแก้ไขในตารางเพื่อแก้ไขรายการ</small>
        </span>

        <!-- Divider -->
        <div class="mx-3" style="border-left: 1px solid #dee2e6; height: 38px;"></div>

        <!-- AI Analysis Button (separated on far right) -->
        <div
          v-b-tooltip.hover
          :title="aiButtonTooltip"
          class="d-inline-block ml-auto"
        >
          <b-button
            variant="success"
            @click="runAIAnalysis"
            :disabled="!canUseAI || isAnalyzing"
          >
            <b-spinner v-if="isAnalyzing" small class="mr-1"></b-spinner>
            <i v-else class="fas fa-robot mr-1"></i>
            {{ isAnalyzing ? 'กำลังวิเคราะห์...' : 'วิเคราะห์ด้วย AI' }}
          </b-button>
        </div>
      </div>

      <!-- Treatment Plan Table -->
      <b-row>
        <b-col cols="12">
          <!-- Bulk Actions Toolbar -->
          <div class="bulk-actions-toolbar mb-3" v-if="treatmentPlan.length > 0">
            <b-button-toolbar>
              <b-button-group class="mr-2">
                <b-button variant="outline-secondary" size="sm" @click="toggleSelectAll">
                  <i class="fas fa-check-square mr-1"></i>
                  {{ allSelected ? 'ยกเลิกเลือกทั้งหมด' : 'เลือกทั้งหมด' }}
                </b-button>
                <b-button 
                  variant="outline-danger" 
                  size="sm" 
                  @click="deleteSelected"
                  :disabled="selectedItems.length === 0"
                >
                  <i class="fas fa-trash mr-1"></i>
                  ลบที่เลือก ({{ selectedItems.length }})
                </b-button>
              </b-button-group>
            </b-button-toolbar>
          </div>
          
          <!-- Treatment Plan Table -->
          <b-card>
            <template #header>
              <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">
                  <i class="fas fa-list mr-2"></i>
                  แผนการรักษา
                </h6>
              </div>
            </template>
          <b-table-simple hover responsive :bordered="false" small show-empty>
            <b-thead>
              <b-tr>
                <b-th width="40">
                  <b-form-checkbox 
                    v-model="allSelected" 
                    @change="toggleSelectAll"
                    v-if="treatmentPlan.length > 0"
                  />
                </b-th>
                <b-th width="40"></b-th>
                <b-th>ซี่ฟัน</b-th>
                <b-th>ด้าน</b-th>
                <b-th>วินิจฉัย</b-th>
                <b-th>การรักษา</b-th>
                <b-th class="text-right">ราคา</b-th>
                <b-th width="100" class="text-center">การดำเนินการ</b-th>
              </b-tr>
            </b-thead>
            <draggable
              :list="treatmentPlan"
              tag="tbody"
              handle=".drag-handle"
              @end="onDragEnd"
            >
              <b-tr 
                v-for="(item, index) in treatmentPlan" 
                :key="item.id || `temp-${index}`"
                :class="{ 
                  'table-info': item.aiGenerated,
                  'table-warning': isEditingInCard && item.id === editingItemId
                }"
              >
                <b-td>
                  <b-form-checkbox 
                    :value="item.id || `temp-${index}`"
                    v-model="selectedItems"
                  />
                </b-td>
                <b-td>
                  <i class="fa fa-bars drag-handle"></i>
                </b-td>
                <b-td>
                  {{ getToothName(item.toothNo) }}
                  <b-badge v-if="item.aiGenerated" variant="info" pill class="ml-1" title="AI Generated">
                    AI
                  </b-badge>
                </b-td>
                <b-td>{{ formatSurfaces(item.toothSurfaces) }}</b-td>
                <b-td>{{ formatDiagnosis(item.diagnosis) }}</b-td>
                <b-td>{{ formatTreatment(item.treatment) }}</b-td>
                <b-td class="text-right">{{ item.price.toLocaleString() }}</b-td>
                <b-td class="text-center">
                  <b-button-group size="sm">
                    <b-button
                      variant="outline-primary"
                      @click="openTreatmentModal('edit', item, index)"
                      title="แก้ไข"
                    >
                      <i class="fa fa-edit"></i>
                    </b-button>
                    <b-button
                      variant="outline-danger"
                      @click="deleteRow(index)"
                      title="ลบ"
                    >
                      <i class="fa fa-trash"></i>
                    </b-button>
                  </b-button-group>
                </b-td>
              </b-tr>
            </draggable>
            <tfoot v-if="treatmentPlan.length > 0">
              <b-tr class="table-secondary">
                <b-td colspan="6" class="text-right">
                  <div class="text-right">
                    <strong>รวมทั้งสิ้น:</strong>
                  </div>
                </b-td>
                <b-td class="text-right font-weight-bold text-primary">
                  {{ totalPrice.toLocaleString() }} ฿
                </b-td>
                <b-td></b-td>
              </b-tr>
            </tfoot>
            <template #empty>
              <div class="text-center text-muted p-4">
                <i class="fas fa-clipboard-list fa-3x mb-3"></i>
                <br>
                ยังไม่มีรายการการรักษา
                <br>
                <small>เลือกซี่ฟันจากแผนภาพแล้วเพิ่มการรักษา</small>
              </div>
            </template>
          </b-table-simple>
          </b-card>
        </b-col>
      </b-row>
    </div>
    
    <!-- Modal Footer for Step 2 (Combined Review + Edit) -->
    <template v-if="currentStep === 2" #modal-footer>
      <b-row class="w-100">
        <b-col cols="6">
          <b-button variant="secondary" @click="goToStep1">
            <i class="fas fa-camera mr-2"></i> กลับไปถ่ายภาพใหม่
          </b-button>
        </b-col>
        <b-col cols="6" class="text-right">
          <b-button variant="success" @click="saveTreatmentPlan">
            <i class="fas fa-save mr-2"></i> บันทึกแผนการรักษา
          </b-button>
        </b-col>
      </b-row>
    </template>
    
    
    <!-- Photo Options Modal -->
    <b-modal
      id="photo-options-modal"
      ref="photoOptionsModal"
      :title="photoOptionsModalTitle"
      size="sm"
      centered
      hide-footer
    >
      <div class="photo-options-content">
        <b-button
          variant="primary"
          size="lg"
          block
          class="mb-3"
          @click="selectCameraOption"
        >
          <i class="fas fa-camera fa-2x mb-2"></i>
          <br>
          ถ่ายภาพ
        </b-button>
        
        <b-button
          variant="success"
          size="lg"
          block
          @click="selectUploadOption"
        >
          <i class="fas fa-upload fa-2x mb-2"></i>
          <br>
          อัปโหลดไฟล์
        </b-button>
      </div>
    </b-modal>

    <!-- Hidden file input for uploads -->
    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png,image/webp,image/heic"
      style="display: none;"
      @change="handleFileSelect($event)"
    />
    
    <!-- Treatment List Modals for Regular Treatment -->
    <TreatDiagListModal
      ref="regularTreatDiagModal"
      @selectedDiagItem="regularDiagnosis = $event"
    />
    <TreatListModal
      ref="regularTreatModal"
      @selectedTreatment="
        regularTreatment = $event;
        regularPrice = $event.price;
      "
    />
    
    
    <Loading v-if="isLoading"></Loading>
    <Dialog ref="Dialog"></Dialog>

    <!-- Treatment Modal (Exact same 2-column layout as before) -->
    <b-modal
      ref="treatmentModal"
      :title="modalTitle"
      size="xl"
      hide-footer
      @hidden="resetTreatmentModal"
    >
      <div class="treatment-modal">
        <!-- Exact same 2-column layout as before -->
        <b-row class="mb-4">
          <!-- AI Photos + Teeth Diagram Column -->
          <b-col cols="12" lg="5" class="sticky-photo-teeth-column">
            <!-- AI Photos with Overlays -->
            <b-card v-if="upperPhotoPreview || lowerPhotoPreview" class="mb-3 ai-photos-card">
              <template #header>
                <h6 class="mb-0">
                  <i class="fas mr-2" :class="analysisResults ? 'fa-robot' : 'fa-images'"></i>
                  {{ analysisResults ? 'AI Analysis' : 'ภาพถ่ายฟัน' }}
                </h6>
              </template>
              <b-row>
                <!-- Upper Photo -->
                <b-col cols="6" v-if="upperPhotoPreview">
                  <div class="photo-with-detections-compact">
                    <h6 class="text-center mb-2 small">ฟันบน</h6>
                    <div class="detection-container-compact">
                      <b-img
                        :src="upperPhotoPreview"
                        fluid
                        class="ai-photo-compact"
                        alt="Upper occlusal with AI detections"
                      />
                      <!-- Problem Overlays (only show if AI analysis available) -->
                      <template v-if="analysisResults">
                        <div
                          v-for="(problem, index) in getProblemsForPhoto('upper')"
                          :key="`modal-upper-${index}`"
                          class="problem-overlay-compact"
                          :style="getProblemOverlayStyle(problem)"
                          :title="`${problem.condition} (${Math.round(problem.confidence * 100)}%)`"
                        />
                      </template>
                    </div>
                  </div>
                </b-col>

                <!-- Lower Photo -->
                <b-col cols="6" v-if="lowerPhotoPreview">
                  <div class="photo-with-detections-compact">
                    <h6 class="text-center mb-2 small">ฟันล่าง</h6>
                    <div class="detection-container-compact">
                      <b-img
                        :src="lowerPhotoPreview"
                        fluid
                        class="ai-photo-compact"
                        alt="Lower occlusal with AI detections"
                      />
                      <!-- Problem Overlays (only show if AI analysis available) -->
                      <template v-if="analysisResults">
                        <div
                          v-for="(problem, index) in getProblemsForPhoto('lower')"
                          :key="`modal-lower-${index}`"
                          class="problem-overlay-compact"
                          :style="getProblemOverlayStyle(problem)"
                          :title="`${problem.condition} (${Math.round(problem.confidence * 100)}%)`"
                        />
                      </template>
                    </div>
                  </div>
                </b-col>
              </b-row>
            </b-card>

            <!-- Teeth Diagram -->
            <b-card class="mb-3">
              <template #header>
                <h6 class="mb-0">
                  <i class="fas fa-tooth mr-2"></i>
                  เลือกซี่ฟัน
                </h6>
              </template>
              <div class="teeth-diagram-container">
                <TeethDiagram
                  id="tooth-diagram-modal"
                  v-model="teethSelected"
                  @hoveringTooth="handleHoveringTooth"
                  @input="onShow"
                  :highlightItems="highlightItems"
                  style="width: 200px; height: auto;"
                />
              </div>
              <div class="selected-teeth-info mt-3" v-if="teethSelected.length > 0">
                <small class="text-muted">ซี่ฟันที่เลือก:</small>
                <b-badge variant="primary" class="ml-2">{{ teethValue }}</b-badge>
              </div>
              <!-- Edit mode restriction warning -->
              <div class="edit-mode-warning mt-2" v-if="isEditingInCard">
                <small class="text-warning">
                  <i class="fas fa-exclamation-triangle mr-1"></i>
                  โหมดแก้ไข: สามารถเลือกได้เพียงซี่ฟันเดียวเท่านั้น
                </small>
              </div>
            </b-card>
          </b-col>

          <!-- Treatment Form Column -->
          <b-col cols="12" lg="7">
            <!-- Ultra-Compact Treatment Summary -->
            <b-card v-if="!isEditingInCard" class="mb-2 treatment-summary-compact">
              <div class="d-flex align-items-center">
                <small class="text-muted mr-2">
                  <i class="fas fa-list mr-1"></i>
                  รายการที่เพิ่มแล้ว ({{ treatmentPlan.length }})
                </small>
                <div class="flex-grow-1">
                  <small v-if="treatmentPlan.length === 0" class="text-muted">
                    ยังไม่มีรายการ
                  </small>
                  <div v-else class="treatment-pills d-flex flex-wrap gap-1">
                    <span
                      v-for="(item, index) in treatmentPlan"
                      :key="index"
                      class="treatment-badge"
                    >
                      <strong>{{ getToothName(item.toothNo) }}</strong> {{ formatTreatment(item.treatment) }}
                    </span>
                  </div>
                </div>
              </div>
            </b-card>

            <!-- Regular Treatment Input Card -->
            <b-card ref="treatmentInputCard" class="mb-3 treatment-input-card" :class="{ 'edit-mode': isEditingInCard }">
              <template #header>
                <h6 class="mb-0" :class="{ 'text-warning': isEditingInCard }">
                  <i class="fas mr-2" :class="isEditingInCard ? 'fa-edit' : 'fa-plus-circle'"></i>
                  <span v-if="isEditingInCard">
                    แก้ไขการรักษา: ซี่ฟัน {{ getEditingToothNumber() }}
                  </span>
                  <span v-else>
                    เพิ่มการรักษา
                  </span>
                </h6>
              </template>
              <div class="treatment-form" :class="{ 'form-muted': teethSelected.length === 0 }">
                <b-form @submit.prevent="onModalTreatmentSubmit">
                  <!-- Selected Teeth Display -->
                  <b-form-input
                    class="mb-2"
                    :value="teethValue"
                    disabled
                    size="sm"
                    readonly
                  />

                  <!-- Tooth Surfaces Selection -->
                  <div class="text-center mb-2">
                    <ToothSurfaces
                      style="width: 150px"
                      :disabled="teethSelected.length === 0"
                      @getToothSurfacesPopover="getRegularToothSurfaces"
                    />
                  </div>

                  <!-- NHSO Filter Options -->
                  <b-form-group
                    v-if="branchLinkedWithNhso"
                    label="กรองรายการ:"
                    label-cols="4"
                    label-align="right"
                    class="my-2"
                  >
                    <b-form-radio-group
                      class="col p-0"
                      v-model="regularSelectedListType"
                      :options="listOptions"
                      buttons
                      button-variant="outline-info"
                      size="sm"
                      name="regular-radio-btn-outline"
                      :disabled="teethSelected.length === 0"
                    />
                  </b-form-group>

                  <!-- Diagnosis Selection -->
                  <b-form-group class="my-2">
                    <b-input-group>
                      <multiselect
                        class="col p-0 expand-dropdown"
                        :class="{
                          'is-invalid': $v.regularDiagnosis.$error,
                          'multiselect-black-border': !$v.regularDiagnosis.$error,
                        }"
                        v-model="$v.regularDiagnosis.$model"
                        :options="currentRegularDiagnosisList"
                        :multiple="false"
                        group-values="libs"
                        group-label="category"
                        :group-select="false"
                        placeholder="พิมพ์ค้นหาการวินิจฉัย"
                        track-by="nameEn"
                        :custom-label="customDxLabel"
                        :disabled="teethSelected.length === 0"
                      >
                        <span slot="noResult">ไม่พบรายการ กรุณาเปลี่ยนคำค้นหา</span>
                        <template #option="props">
                          <span v-if="props.option.$isLabel">
                            {{ props.option.$groupLabel }}
                          </span>
                          <span v-else>
                            <b-button
                              class="mr-1 small"
                              size="xs"
                              variant="outline-info"
                              :pressed="props.option.favorite"
                              @click.stop="favDxToggle(props.option)"
                            >
                              <i class="far fa-star"></i>
                            </b-button>
                            {{ customDxLabel(props.option) }}
                          </span>
                        </template>
                      </multiselect>
                      <b-input-group-append>
                        <b-button variant="info" @click="openRegularTreatDiagModal" size="sm" :disabled="teethSelected.length === 0">
                          Dx. list
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                    <div v-if="$v.regularDiagnosis.$error" class="text-danger small mt-1">
                      *กรุณาเลือกการวินิจฉัย
                    </div>
                  </b-form-group>

                  <!-- Treatment Selection -->
                  <b-form-group class="mb-2">
                    <b-input-group>
                      <multiselect
                        class="col p-0 expand-dropdown"
                        :class="{
                          'is-invalid': $v.regularTreatment.$error,
                          'multiselect-black-border': !$v.regularTreatment.$error,
                        }"
                        v-model="$v.regularTreatment.$model"
                        :options="currentRegularTreatmentList"
                        :multiple="false"
                        group-values="libs"
                        group-label="category"
                        :group-select="false"
                        placeholder="พิมพ์ค้นหาการรักษา"
                        :custom-label="customTxDrugsLabel"
                        track-by="nameTh"
                        @select="focusRegularPriceInput"
                        :disabled="teethSelected.length === 0"
                      >
                        <template #option="props">
                          <span v-if="props.option.$isLabel">
                            {{ props.option.$groupLabel }}
                          </span>
                          <span v-else>
                            <b-button
                              class="mr-1 small"
                              size="xs"
                              variant="outline-info"
                              :pressed="props.option.favorite"
                              @click.stop="favTxToggle(props.option)"
                            >
                              <i class="far fa-star"></i>
                            </b-button>
                            {{ customTxDrugsLabel(props.option) }}
                          </span>
                        </template>
                        <span slot="noResult">ไม่พบรายการ กรุณาเปลี่ยนคำค้นหา</span>
                      </multiselect>
                      <b-input-group-append>
                        <b-button variant="info" @click="openRegularTreatModal" size="sm" :disabled="teethSelected.length === 0">
                          Tx. list
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                    <div v-if="$v.regularTreatment.$error" class="text-danger small mt-1">
                      *กรุณาเลือกการรักษา
                    </div>
                  </b-form-group>

                  <!-- Price Input -->
                  <b-form-group>
                    <b-input-group append="บาท/ซี่" size="sm">
                      <b-form-input
                        ref="regularTreatmentPriceInput"
                        type="number"
                        placeholder="฿"
                        v-model="regularPrice"
                        number
                        lazy-formatter
                        :disabled="teethSelected.length === 0"
                      />
                    </b-input-group>
                  </b-form-group>

                  <!-- Detail Textarea -->
                  <b-form-row class="mb-2">
                    <div class="col">
                      <b-form-textarea
                        v-model="regularDetail"
                        placeholder="รายละเอียดเพิ่มเติม"
                        rows="3"
                        size="sm"
                        no-resize
                        lazy
                        :disabled="teethSelected.length === 0"
                      />
                    </div>
                  </b-form-row>

                  <!-- Action Buttons -->
                  <div class="text-right">
                    <!-- Edit Mode Buttons -->
                    <template v-if="isEditingInCard">
                      <b-button class="mr-2" @click="cancelModalEdit" size="sm" variant="outline-secondary">
                        ยกเลิก
                      </b-button>
                      <b-button @click="saveModalEdit" size="sm" variant="warning">
                        บันทึกการแก้ไข
                      </b-button>
                    </template>

                    <!-- Add Mode Buttons -->
                    <template v-else>
                      <b-button class="mr-2" @click="clearRegularForm" size="sm" variant="outline-secondary">
                        ล้าง
                      </b-button>
                      <b-button type="submit" size="sm" variant="primary" :disabled="teethSelected.length === 0">
                        เพิ่ม
                      </b-button>
                    </template>
                  </div>
                </b-form>

                <!-- Helper messages -->
                <div v-if="teethSelected.length === 0" class="text-center text-muted mt-3">
                  <small>เลือกซี่ฟันเพื่อเพิ่มการรักษา</small>
                </div>

                <!-- Edit mode specific warning for multiple teeth -->
                <div v-if="isEditingInCard && teethSelected.length > 1" class="text-center text-warning mt-3">
                  <small>
                    <i class="fas fa-exclamation-triangle mr-1"></i>
                    โหมดแก้ไข: จะใช้เฉพาะซี่ฟัน {{ teethSelected[0].nameTh || teethSelected[0].name }}
                  </small>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </b-modal>
</template>

<style scoped>
.teeth-diagram-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  padding: 10px;
}

.selected-teeth-info {
  border-top: 1px solid #dee2e6;
  padding-top: 15px;
}

/* Ensure the teeth diagram is visible and properly sized */
.teeth-diagram-container svg {
  max-width: 100%;
  height: auto;
}

/* Sticky photo + teeth column for md and up */
@media (min-width: 768px) {
  .sticky-photo-teeth-column {
    position: sticky;
    top: 10px;
    max-height: 90vh;
    overflow-y: auto;
  }
}

/* AI Photos Compact Styling */
.ai-photo-compact {
  width: 100%;
  max-height: 150px;
  object-fit: cover;
  border-radius: 4px;
}

/* Detection container for compact photos */
.detection-container-compact {
  position: relative;
  width: 100%;
}

/* Problem overlays for compact photos */
.problem-overlay-compact {
  position: absolute;
  border: 2px solid #dc3545;
  background-color: rgba(220, 53, 69, 0.1);
  border-radius: 4px;
  pointer-events: none;
}

/* Mobile: Make only AI photos sticky, not the whole column */
@media (max-width: 767.98px) {
  .sticky-photo-teeth-column {
    position: static;
  }

  .ai-photos-card {
    position: sticky !important;
    top: 0;
    z-index: 100;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .ai-photo-compact {
    max-height: 100px;
  }
}

/* Mobile responsive adjustments */
@media (max-width: 991.98px) {
  .teeth-diagram-container {
    min-height: 180px;
  }
}

/* Treatment form styling */
.treatment-form-card {
  border: 1px solid #e3f2fd;
}

.treatment-list-card {
  border: 1px solid #f3e5f5;
}

/* Enhanced table styling */
.table th {
  background-color: #f8f9fa;
  border-top: none;
  font-weight: 600;
}

.drag-handle {
  cursor: grab;
  color: #6c757d;
}

.drag-handle:hover {
  color: #495057;
}

/* Simplified Photo Input Styles */
.photo-input-sections {
  max-width: 700px;
  margin: 0 auto;
}

.photo-input-row {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

/* Unified Photo Placeholder */
.unified-photo-placeholder {
  border: 3px dashed #dee2e6;
  border-radius: 16px;
  padding: 50px 30px;
  text-align: center;
  cursor: pointer;
  background-color: #f8f9fa;
  transition: all 0.4s ease;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.unified-photo-placeholder:hover,
.unified-photo-placeholder.hover-active {
  border-color: #007bff;
  background-color: #e3f2fd;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,123,255,0.15);
}

.unified-photo-placeholder.drag-active {
  border-color: #28a745;
  background-color: #d4edda;
  border-style: solid;
  transform: scale(1.05);
  box-shadow: 0 12px 30px rgba(40,167,69,0.2);
}

.placeholder-content {
  max-width: 100%;
}

.placeholder-content i {
  transition: all 0.3s ease;
}

.unified-photo-placeholder:hover .placeholder-content i {
  transform: scale(1.1);
}

/* Photo Display Styles */
.captured-photo-display {
  text-align: center;
}

.photo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-height: 400px;
  overflow: hidden;
  margin: 0 auto;
  position: relative;
}

.captured-photo-preview {
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  object-fit: contain;
}

.photo-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
}

/* Photo Options Modal Styles */
.photo-options-content {
  padding: 10px 0;
}

.photo-options-content .btn {
  padding: 20px;
  border-radius: 12px;
  font-weight: 600;
}

.photo-options-content .btn i {
  display: block;
  margin-bottom: 8px;
}

/* AI Detection Visualization Styles */
.photo-with-detections {
  text-align: center;
}

.detection-container {
  position: relative;
  display: inline-block;
  max-width: 100%;
}

.detection-base-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.problem-overlay {
  position: absolute;
  pointer-events: none;
  animation: pulse-detection 2s infinite;
}

.problem-label {
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0,0,0,0.9);
  color: white;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: bold;
  white-space: nowrap;
  pointer-events: none;
  z-index: 11;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

@keyframes pulse-detection {
  0% { 
    opacity: 0.7;
    transform: scale(1);
  }
  50% { 
    opacity: 1;
    transform: scale(1.02);
  }
  100% { 
    opacity: 0.7;
    transform: scale(1);
  }
}

.problems-legend {
  border-top: 1px solid #dee2e6;
  padding-top: 15px;
}

.problem-items {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .unified-photo-placeholder {
    padding: 40px 20px;
    min-height: 180px;
  }
  
  .placeholder-content h5 {
    font-size: 1.1rem;
  }
  
  .placeholder-content p {
    font-size: 0.9rem;
  }
  
  .detection-container {
    max-width: 100%;
  }
  
  .problem-label {
    font-size: 0.6rem;
    padding: 1px 4px;
    bottom: -20px;
  }
  
  .problem-overlay {
    border-width: 1px;
  }
}

/* Camera Modal Styles */
.simple-camera-container {
  text-align: center;
}

.camera-preview-container {
  position: relative;
  display: inline-block;
  max-width: 100%;
}

.camera-preview {
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
  background-color: #000;
}

.camera-error {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  color: #856404;
}

.review-photo {
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.review-actions {
  padding: 20px 0;
}

/* Step actions */
.step-actions {
  border-top: 1px solid #dee2e6;
  padding-top: 20px;
  margin-top: 20px;
}

/* Enhanced Treatment Plan Styles */
.ai-status-header {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.bulk-actions-toolbar {
  background-color: #f8f9fa;
  padding: 10px;
  border-radius: 8px;
  margin-bottom: 15px;
}

.table-info {
  background-color: #e3f2fd !important;
}

.table-info:hover {
  background-color: #bbdefb !important;
}

.quick-actions .btn-group-vertical .btn {
  text-align: left;
  padding: 8px 12px;
  margin-bottom: 5px;
}

.quick-actions .btn-group-vertical .btn:hover {
  transform: translateX(3px);
  transition: transform 0.2s ease;
}

/* Treatment Input Card Styling */
.treatment-input-card {
  border: 1px solid #e3f2fd;
  background: #f8f9fa;
}

.treatment-input-card .card-header {
  background: #e3f2fd;
  border-bottom: 1px solid #bbdefb;
}

/* Edit Mode Styling */
.treatment-input-card.edit-mode {
  border: 2px solid #ffc107;
  background: #fff9e6;
  box-shadow: 0 4px 8px rgba(255, 193, 7, 0.3);
  transition: all 0.3s ease;
}
.treatment-input-card.edit-mode .card-header {
  background: #fff3cd;
  border-bottom: 1px solid #ffc107;
}

/* Edit Mode Warning */
.edit-mode-warning {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  padding: 8px 12px;
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Muted Form State */
.form-muted {
  opacity: 0.6;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.form-muted .form-control,
.form-muted .multiselect,
.form-muted .btn {
  background-color: #f8f9fa !important;
  border-color: #dee2e6 !important;
  color: #6c757d !important;
}

.form-muted .multiselect__tags {
  background-color: #f8f9fa !important;
  border-color: #dee2e6 !important;
}


.treatment-form .form-group {
  margin-bottom: 0.5rem;
}

.treatment-form .multiselect {
  font-size: 0.8rem;
}

.treatment-form .multiselect__content-wrapper {
  font-size: 0.8rem;
}

/* Action toolbar */
.action-toolbar {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
  margin-bottom: 1rem;
}

.multiselect-black-border .multiselect__tags {
  border: 1px solid #ced4da;
}

.multiselect-black-border .multiselect__tags:focus-within {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

/* Treatment table enhancements */
.table tbody tr {
  transition: all 0.2s ease;
}

.table tbody tr:hover {
  transform: scale(1.01);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.table-secondary {
  background-color: #f8f9fa;
  font-weight: 600;
}

/* Modal styles */
.modal-body .form-group label {
  font-weight: 600;
  color: #495057;
}

/* Badge animations */
.badge {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Expand dropdown styling from original TreatmentPlan */
.expand-dropdown > .multiselect__content-wrapper {
  font-size: 0.8rem;
  position: absolute;
  left: 0;
  min-width: 100%;
  width: 80vw; /* Default to full width on smaller screens */
}

@media (min-width: 480px) {
  .expand-dropdown > .multiselect__content-wrapper {
    width: calc(100vw - 50%);
  }
}

/* Small screens */
@media (min-width: 640px) {
  .expand-dropdown > .multiselect__content-wrapper {
    width: calc(100vw - 180%);
  }
}

/* Medium screens */
@media (min-width: 768px) {
  .expand-dropdown > .multiselect__content-wrapper {
    width: calc(100vw - 200%);
  }
}

/* Large screens */
@media (min-width: 1024px) {
  .expand-dropdown > .multiselect__content-wrapper {
    width: calc(100vw - 200%);
  }
}

/* Extra large screens */
@media (min-width: 1280px) {
  .expand-dropdown > .multiselect__content-wrapper {
    width: calc(100vw - 250%);
  }
}

/* Quality Check Panel Styles */
.quality-check-panel {
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.quality-check-panel .card {
  margin-bottom: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.quality-check-panel .card-body {
  padding: 12px 16px;
}

.quality-recommendations {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
  font-size: 0.9rem;
}

.quality-recommendations li {
  padding: 4px 0;
  color: #495057;
}

.quality-recommendations li::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 4px;
  background-color: currentColor;
  border-radius: 50%;
  margin-right: 8px;
  vertical-align: middle;
}

/* Quality check in progress indicator */
.quality-check-panel + .text-center {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

/* Crop mode styles */
.photo-container {
  position: relative;
  display: inline-block;
  max-width: 100%;
}

.photo-container.crop-mode-active {
  cursor: crosshair;
}

.crop-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
}

.crop-overlay-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  pointer-events: none;
}

.crop-rectangle {
  position: absolute;
  border: 2px solid #007bff;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
  cursor: move;
  background: transparent;
}

.crop-grid {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.crop-grid-line {
  position: absolute;
  background-color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
}

.crop-grid-v {
  width: 1px;
  height: 100%;
  top: 0;
}

.crop-grid-h {
  height: 1px;
  width: 100%;
  left: 0;
}

.crop-handle {
  position: absolute;
  width: 12px;
  height: 12px;
  background-color: #007bff;
  border: 2px solid white;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.3);
  z-index: 20;
}

/* Corner handles */
.crop-handle-nw {
  top: -6px;
  left: -6px;
  cursor: nw-resize;
}

.crop-handle-ne {
  top: -6px;
  right: -6px;
  cursor: ne-resize;
}

.crop-handle-sw {
  bottom: -6px;
  left: -6px;
  cursor: sw-resize;
}

.crop-handle-se {
  bottom: -6px;
  right: -6px;
  cursor: se-resize;
}

/* Edge handles */
.crop-handle-n {
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  cursor: n-resize;
}

.crop-handle-s {
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  cursor: s-resize;
}

.crop-handle-e {
  right: -6px;
  top: 50%;
  transform: translateY(-50%);
  cursor: e-resize;
}

.crop-handle-w {
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  cursor: w-resize;
}

.crop-controls {
  text-align: center;
}
</style>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import TeethDiagram from '@/components/popover/TeethDiagram';
import ToothSurfaces from '@/components/popover/ToothSurfaces';
import Loading from '@/components/Loading';
import Dialog from '@/components/modal/Dialog';
import Draggable from 'vuedraggable';
import Multiselect from 'vue-multiselect';
import TreatDiagListModal from '@/components/modal/TreatDiagList';
import TreatListModal from '@/components/modal/TreatList';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { applyImageTransformations, applyCrop, base64ToFile } from '@/utils/imageTransformation';
import { analyzeImageQuality, getQualityBadgeVariant, getQualityIcon } from '@/utils/imageQualityCheck';

export default {
  name: 'EnhancedTreatmentPlan',
  
  components: {
    TeethDiagram,
    ToothSurfaces,
    Loading,
    Dialog,
    Draggable,
    Multiselect,
    TreatDiagListModal,
    TreatListModal
  },
  
  data() {
    return {
      // Workflow - now 2 steps (AI analysis is optional)
      steps: [
        { number: 1, title: 'ถ่ายภาพ', icon: 'fas fa-camera' },
        { number: 2, title: 'แผนการรักษา', icon: 'fas fa-edit' }
      ],
      
      // Patient data
      patient: {},
      patientId: null,
      
      // Treatment plan editing (Step 4)
      treatmentPlan: [],
      planSelected: {}, // Store full plan object (like TreatmentPlan.vue)
      teethSelected: [],
      toothSurfaces: [],
      diagnosis: null,
      treatment: null,
      price: 0,
      detail: "",
      
      hoveringTooth: {},
      selectedToothAISuggestion: null,
      
      // Camera modal state
      currentCameraType: null, // 'upper' or 'lower'
      cameraReady: false,
      cameraError: null,
      photoTakenInModal: false,

      // Image transformation state
      upperPhotoTransformations: {
        rotation: 0,
        flipHorizontal: false,
        flipVertical: false,
        cropArea: null
      },
      lowerPhotoTransformations: {
        rotation: 0,
        flipHorizontal: false,
        flipVertical: false,
        cropArea: null
      },

      // Crop mode state
      cropMode: {
        active: false,
        type: null, // 'upper' or 'lower'
        cropArea: { x: 10, y: 10, width: 80, height: 80 }, // Default crop area (percentages)
        isDragging: false,
        isResizing: false,
        resizeHandle: null, // 'nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'
        dragStart: { x: 0, y: 0 }
      },
      capturedPhotoUrlInModal: null,
      mediaStream: null,

      // Camera switching state
      currentFacingMode: 'environment', // 'environment' (back) or 'user' (front)
      availableCameras: [],
      switchingCamera: false,
      
      // File upload state
      dragActive: {
        upper: false,
        lower: false
      },
      hoverActive: {
        upper: false,
        lower: false
      },
      currentUploadType: null,
      
      // Image dimensions tracking for proper overlay positioning
      imageDimensions: {
        upper: { width: 1920, height: 1080 }, // Default dimensions
        lower: { width: 1920, height: 1080 }
      },
      
      // Enhanced treatment editing
      selectedItems: [], // For bulk selection
      allSelected: false,
      
      // Card-based edit state
      isEditingInCard: false,
      editingItemId: null,
      originalCardData: null,
      
      // UI state
      isLoading: false,
      isProcessing: false, // For upload and analysis process
      isAnalyzing: false, // For optional AI analysis in step 2

      // Image Quality Check State
      upperPhotoQuality: null,
      lowerPhotoQuality: null,
      qualityCheckInProgress: false,

      // Regular Treatment Input
      regularToothSurfaces: [],
      regularDiagnosis: null,
      regularTreatment: null,
      regularPrice: 0,
      regularDetail: "",
      regularSelectedListType: localStorage.getItem("selectedListType") || "Plan",
      
      
      // List options for NHSO filtering
      listOptions: [
        { text: "ทั้งหมด", value: "Plan" },
        { text: "สปสช", value: "NHSO" },
        { text: "คลินิก", value: "General" },
      ],

      // Navigation lock state
      isNavigationLocked: false,
      preventNavigationHandler: null,

      // Existing data loading state
      isLoadingExisting: false,
      loadedPhotosCount: 0,
      stepsCompleted: [false, false] // Track completion of 2 steps
    };
  },
  
  validations: {
    regularDiagnosis: {
      required: requiredIf(function () {
        return this.branchLinkedWithNhso;
      }),
    },
    regularTreatment: { required }
  },
  
  computed: {
    ...mapState('dentalAI', [
      'currentStep',
      'analysisInProgress',
      'analysisResults',
      'analysisError',
      'autoGeneratedPlan'
    ]),
    
    ...mapState('protocolMedia', [
      'uploading',
      'uploadError',
      'totalUploadProgress',
      'protocolProgress'
    ]),
    
    ...mapGetters({
      hasAllPhotos: 'dentalAI/hasAllPhotos',
      canProceedToNextStep: 'protocolMedia/canProceedToNextStep',
      isAnalysisComplete: 'dentalAI/isAnalysisComplete',
      // Protocol media getters
      upperPhotoPreview: 'protocolMedia/upperPhotoPreview',
      lowerPhotoPreview: 'protocolMedia/lowerPhotoPreview',
      upperPhotoMediaUrl: 'protocolMedia/upperPhotoMediaUrl',
      lowerPhotoMediaUrl: 'protocolMedia/lowerPhotoMediaUrl',
      hasLocalPhotos: 'protocolMedia/hasLocalPhotos',
      localPhotoCount: 'protocolMedia/localPhotoCount',
      uploadedPhotoCount: 'protocolMedia/uploadedPhotoCount',
      photosReadyForUpload: 'protocolMedia/photosReadyForUpload',
      canEditPhotos: 'protocolMedia/canEditPhotos',
      isUploading: 'protocolMedia/isUploading',
      uploadProgressPercentage: 'protocolMedia/uploadProgressPercentage',
      getToothBy: 'moduleMaster/getToothBy',
      getToothSurfaceBy: 'moduleMaster/getToothSurfaceBy',
      getToothList: 'moduleMaster/getToothList',
      getToothSurfaceList: 'moduleMaster/getToothSurfaceList',
      getTreatBy: 'moduleAppointment/getTreatBy',
      getTreatDiagBy: 'moduleAppointment/getTreatDiagBy',
      getTreatList: 'moduleAppointment/getTreatList',
      getTreatDiagList: 'moduleAppointment/getTreatDiagList',
      getNhsoMtreatIds: 'moduleAppointment/getNhsoMtreatIds',
      getGroupedTreatListPlan: 'moduleAppointment/getGroupedTreatListPlan',
      getGroupedTreatDiagListPlan: 'moduleAppointment/getGroupedTreatDiagListPlan',
      getGroupedTreatListNhso: 'moduleAppointment/getGroupedTreatListNhso',
      getGroupedTreatDiagListNhso: 'moduleAppointment/getGroupedTreatDiagListNhso',
      getGroupedTreatList: 'moduleAppointment/getGroupedTreatList',
      getGroupedTreatDiagList: 'moduleAppointment/getGroupedTreatDiagList',
      branchLinkedWithNhso: 'moduleUser/getLinkedWithNhso'
    }),
    
    progressVariant() {
      if (this.currentStep === 2) return 'success';
      return 'primary';
    },

    // Check if AI analysis is available (only for new base64 uploads, not S3 URLs)
    canUseAI() {
      const upperIsBase64 = this.upperPhotoPreview?.startsWith('data:');
      const lowerIsBase64 = this.lowerPhotoPreview?.startsWith('data:');

      // Both photos must exist AND both must be base64 (new uploads)
      // S3 URLs won't work due to CORS policy
      return upperIsBase64 && lowerIsBase64;
    },

    // Tooltip message for AI button when disabled
    aiButtonTooltip() {
      if (this.isAnalyzing) {
        return 'กำลังวิเคราะห์ภาพ...';
      }

      const upperIsBase64 = this.upperPhotoPreview?.startsWith('data:');
      const lowerIsBase64 = this.lowerPhotoPreview?.startsWith('data:');

      if (!this.upperPhotoPreview || !this.lowerPhotoPreview) {
        return 'กรุณาถ่ายภาพฟันบนและฟันล่างก่อนวิเคราะห์';
      }

      if (!upperIsBase64 || !lowerIsBase64) {
        return 'รองรับเฉพาะภาพที่ถ่ายใหม่เท่านั้น';
      }

      return '';
    },

    highlightItems() {
      return this.treatmentPlan?.map(i => i?.toothNo);
    },
    
    totalPrice() {
      return this.treatmentPlan.reduce((total, item) => total + (item.price || 0), 0);
    },
    
    teethValue() {
      if (!this.teethSelected || this.teethSelected.length === 0) {
        return '';
      }
      return this.teethSelected
        .filter(tooth => tooth && tooth.name) // Remove empty/invalid items
        .map(tooth => tooth.name)
        .join(", ");
    },
    
    diagnosisOptions() {
      // Use real database data from getTreatDiagList
      if (!this.getTreatDiagList || this.getTreatDiagList.length === 0) {
        return [];
      }
      
      return this.getTreatDiagList.map(diagnosis => ({
        value: diagnosis,
        text: diagnosis.code ? `[${diagnosis.code}] ${diagnosis.nameEn || diagnosis.nameTh}` : (diagnosis.nameEn || diagnosis.nameTh)
      })).sort((a, b) => {
        // Prioritize ICD-10 K-codes
        const aHasKCode = a.value.code && a.value.code.startsWith('K');
        const bHasKCode = b.value.code && b.value.code.startsWith('K');
        if (aHasKCode && !bHasKCode) return -1;
        if (!aHasKCode && bHasKCode) return 1;
        return a.text.localeCompare(b.text);
      });
    },
    
    treatmentOptions() {
      // Use real database data from getTreatList with NHSO filtering
      if (!this.getTreatList || this.getTreatList.length === 0) {
        return [];
      }
      
      const nhsoApprovedIds = this.getNhsoMtreatIds || [];
      const treatmentList = this.getTreatList;
      
      // Filter to NHSO approved treatments if available
      const filteredTreatments = nhsoApprovedIds.length > 0 
        ? treatmentList.filter(treatment => nhsoApprovedIds.includes(treatment.id))
        : treatmentList;
      
      return filteredTreatments.map(treatment => ({
        value: treatment,
        text: treatment.code ? `[${treatment.code}] ${treatment.nameTh || treatment.name}` : (treatment.nameTh || treatment.name)
      })).sort((a, b) => {
        // Prioritize treatments with codes
        const aHasCode = !!a.value.code;
        const bHasCode = !!b.value.code;
        if (aHasCode && !bHasCode) return -1;
        if (!aHasCode && bHasCode) return 1;
        return a.text.localeCompare(b.text);
      });
    },
    
    cameraModalTitle() {
      return this.currentCameraType === 'upper'
        ? 'ถ่ายภาพฟันบน (Upper Occlusal)'
        : 'ถ่ายภาพฟันล่าง (Lower Occlusal)';
    },

    modalTitle() {
      return this.isEditingInCard ? 'แก้ไขการรักษา' : 'เพิ่มการรักษา';
    },
    
    aiGeneratedCount() {
      return this.treatmentPlan.filter(item => item.aiGenerated).length;
    },
    
    manualCount() {
      return this.treatmentPlan.filter(item => !item.aiGenerated).length;
    },
    
    // Database integration statistics
    databaseIntegratedCount() {
      return this.treatmentPlan.filter(item => item.aiGenerated && !item.isFallback).length;
    },
    
    fallbackTreatmentCount() {
      return this.treatmentPlan.filter(item => item.aiGenerated && item.isFallback).length;
    },
    
    // Treatment plan statistics with database information
    treatmentPlanStats() {
      const aiTreatments = this.treatmentPlan.filter(item => item.aiGenerated);
      const databaseTreatments = aiTreatments.filter(item => !item.isFallback);
      const fallbackTreatments = aiTreatments.filter(item => item.isFallback);
      
      return {
        total: this.treatmentPlan.length,
        manual: this.treatmentPlan.filter(item => !item.aiGenerated).length,
        aiGenerated: aiTreatments.length,
        databaseBased: databaseTreatments.length,
        fallback: fallbackTreatments.length,
        averageConfidence: aiTreatments.length > 0 ? 
          (aiTreatments.reduce((sum, item) => sum + (item.aiConfidence || 0), 0) / aiTreatments.length).toFixed(2) : 0,
        highConfidenceTreatments: aiTreatments.filter(item => (item.aiConfidence || 0) > 0.8).length,
        totalPrice: this.totalPrice,
        databaseCodes: databaseTreatments.map(item => `${item.diagnosisCode}→${item.treatmentCode}`).filter(Boolean)
      };
    },
    
    availableTeeth() {
      // Use real database data for teeth options
      if (!this.getToothList || this.getToothList.length === 0) {
        // Fallback to hardcoded teeth if database data not available
        const teeth = [];
        for (let i = 11; i <= 18; i++) teeth.push({ value: i, text: `#${i}` });
        for (let i = 21; i <= 28; i++) teeth.push({ value: i, text: `#${i}` });
        for (let i = 31; i <= 38; i++) teeth.push({ value: i, text: `#${i}` });
        for (let i = 41; i <= 48; i++) teeth.push({ value: i, text: `#${i}` });
        return teeth;
      }
      
      return this.getToothList.map(tooth => ({
        value: tooth.id,
        text: tooth.name ? `#${tooth.name}` : `#${tooth.id}`
      }));
    },
    
    photoOptionsModalTitle() {
      return this.currentUploadType === 'upper'
        ? 'เลือกวิธีเพิ่มภาพฟันบน'
        : 'เลือกวิธีเพิ่มภาพฟันล่าง';
    },

    isUpperPhotoRotatedVertical() {
      const rotation = this.upperPhotoTransformations.rotation;
      return rotation === 90 || rotation === 270;
    },

    isLowerPhotoRotatedVertical() {
      const rotation = this.lowerPhotoTransformations.rotation;
      return rotation === 90 || rotation === 270;
    },

    // Check if photos are existing photos from S3 (have mediaURL)
    isUpperPhotoExisting() {
      // Existing photo = has mediaURL (from S3) AND no new file (not newly captured/uploaded)
      const upperPhoto = this.$store.state.protocolMedia?.localPhotos?.upper;
      const hasNewFile = !!upperPhoto?.file;
      const hasMediaUrl = !!this.upperPhotoMediaUrl;

      // Only disable if it's an old photo (has mediaURL) and NOT a new photo (no file)
      return hasMediaUrl && !hasNewFile;
    },

    isLowerPhotoExisting() {
      // Existing photo = has mediaURL (from S3) AND no new file (not newly captured/uploaded)
      const lowerPhoto = this.$store.state.protocolMedia?.localPhotos?.lower;
      const hasNewFile = !!lowerPhoto?.file;
      const hasMediaUrl = !!this.lowerPhotoMediaUrl;

      // Only disable if it's an old photo (has mediaURL) and NOT a new photo (no file)
      return hasMediaUrl && !hasNewFile;
    },

    hasNewPhotos() {
      // Check if we have photos ready for upload/analysis
      // Supports: 1) Both new photos, 2) One new photo + one existing photo
      if (!this.$store.state.protocolMedia) return false;

      const upperPhoto = this.$store.state.protocolMedia.localPhotos?.upper;
      const lowerPhoto = this.$store.state.protocolMedia.localPhotos?.lower;

      // Check if we have both photos (either new .file or existing .base64)
      const hasUpperPhoto = upperPhoto?.file || upperPhoto?.base64;
      const hasLowerPhoto = lowerPhoto?.file || lowerPhoto?.base64;

      // Check if at least one photo is new (has .file)
      const hasAnyNewFile = upperPhoto?.file || lowerPhoto?.file;

      // Return true if: we have both photos AND at least one is new
      return hasUpperPhoto && hasLowerPhoto && hasAnyNewFile;
    },

    // Debug information for database integration
    debugInfo() {
      if (process.env.NODE_ENV !== 'development') return null;

      return {
        step: this.currentStep,
        hasPhotos: this.hasAllPhotos,
        analysisComplete: this.isAnalysisComplete,
        autoGeneratedPlan: this.autoGeneratedPlan?.length || 0,
        treatmentPlanLength: this.treatmentPlan.length,
        databaseStats: this.treatmentPlanStats
      };
    },
    
    // Regular treatment lists
    currentRegularTreatmentList() {
      const listMap = {
        Plan: "getGroupedTreatListPlan",
        NHSO: "getGroupedTreatListNhso",
        General: "getGroupedTreatList",
      };
      return this[listMap[this.regularSelectedListType]];
    },
    
    currentRegularDiagnosisList() {
      const listMap = {
        Plan: "getGroupedTreatDiagListPlan",
        NHSO: "getGroupedTreatDiagListNhso",
        General: "getGroupedTreatDiagList",
      };
      return this[listMap[this.regularSelectedListType]];
    },

    // Camera-related computed properties
    cameraModeName() {
      return this.currentFacingMode === 'environment' ? 'กล้องหลัง' : 'กล้องหน้า';
    },

    canSwitchCamera() {
      return this.availableCameras.length > 1 && this.cameraReady && !this.switchingCamera;
    },

    // Mobile device detection
    isMobileDevice() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },

    // Treatment plan mode helpers (like TreatmentPlan.vue)
    canCreatePlan() {
      return !this.planSelected?.id;
    },

    canEditPlan() {
      return Boolean(this.planSelected?.id);
    }

  },

  watch: {
    // Restrict multiple tooth selection in edit mode
    teethSelected: {
      handler(newVal) {
        if (this.isEditingInCard && newVal && newVal.length > 1) {
          // In edit mode, restrict to single tooth selection
          // Keep only the most recently selected tooth
          const mostRecentTooth = newVal[newVal.length - 1];
          this.$nextTick(() => {
            this.teethSelected = [mostRecentTooth];
          });
        }
      },
      immediate: false
    }
  },

  methods: {
    ...mapActions({
      analyzeDentalPhotos: 'dentalAI/analyzeDentalPhotos',
      nextStepAction: 'dentalAI/nextStep',
      previousStepAction: 'dentalAI/previousStep',
      resetWorkflow: 'dentalAI/resetWorkflow',
      createTreatmentPlan: 'moduleTreatmentPlan/createTreatmentPlan',
      updateTreatmentPlan: 'moduleTreatmentPlan/updateTreatmentPlan',
      // ProtocolMedia actions
      initializeWorkflow: 'protocolMedia/initializeWorkflow',
      saveLocalPhoto: 'protocolMedia/saveLocalPhoto',
      clearLocalPhoto: 'protocolMedia/clearLocalPhoto',
      batchUploadPhotos: 'protocolMedia/batchUploadPhotos',
      refreshProtocolMedia: 'protocolMedia/refreshProtocolMedia',
      resetProtocolMedia: 'protocolMedia/resetWorkflow'
    }),

    // Image debugging event handlers
    handleImageLoad() {
      // Image loaded successfully
    },

    handleImageError(type, event) {
      console.error(`❌ Image failed to load for ${type}:`, event.target.src);
      console.error('🔍 Error details:', event);
      // Try to show more detailed error information
      if (event.target.src) {
        console.error('🌐 Testing URL accessibility...');
        fetch(event.target.src, { method: 'HEAD', mode: 'no-cors' })
          .catch(err => console.error('💥 URL fetch failed:', err));
      }
    },

    // Card-based edit helper methods
    getEditingToothNumber() {
      if (!this.isEditingInCard || !this.editingItemId) return '';
      const editingItem = this.treatmentPlan.find(item => item.id === this.editingItemId);
      return editingItem ? editingItem.toothNo : '';
    },
    
    // Enter card-based edit mode
    enterCardEditMode(item) {
      // Store original data for cancel functionality
      this.originalCardData = {
        regularToothSurfaces: [...this.regularToothSurfaces],
        regularDiagnosis: this.regularDiagnosis,
        regularTreatment: this.regularTreatment,
        regularPrice: this.regularPrice,
        regularDetail: this.regularDetail,
        teethSelected: [...this.teethSelected]
      };
      
      // Set edit state
      this.isEditingInCard = true;
      this.editingItemId = item.id;
      
      // Populate form with item data
      this.populateCardForm(item);
      
      // Scroll to card
      this.$nextTick(() => {
        if (this.$refs.treatmentInputCard && this.$refs.treatmentInputCard.$el) {
          this.$refs.treatmentInputCard.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    
    // Populate card form with item data
    populateCardForm(item) {
      // Set selected teeth - get proper tooth data from master data
      if (item.toothNo) {
        const toothData = this.getToothBy(item.toothNo, 'id');
        this.teethSelected = toothData ? [toothData] : [];
      } else {
        this.teethSelected = [];
      }
      
      // Set tooth surfaces
      this.regularToothSurfaces = Array.isArray(item.toothSurfaces) ? 
        item.toothSurfaces.map(surface => ({ id: surface })) : [];
      
      // Set diagnosis - handle both object and ID formats
      if (typeof item.diagnosis === 'object') {
        this.regularDiagnosis = item.diagnosis;
      } else {
        // Find diagnosis object from list by ID
        const diagnosisList = this.currentRegularDiagnosisList;
        for (const group of diagnosisList) {
          const found = group.libs.find(d => d.id === item.diagnosis);
          if (found) {
            this.regularDiagnosis = found;
            break;
          }
        }
      }
      
      // Set treatment - handle both object and ID formats  
      if (typeof item.treatment === 'object') {
        this.regularTreatment = item.treatment;
      } else {
        // Find treatment object from list by ID
        const treatmentList = this.currentRegularTreatmentList;
        for (const group of treatmentList) {
          const found = group.libs.find(t => t.id === item.treatment);
          if (found) {
            this.regularTreatment = found;
            break;
          }
        }
      }
      
      // Set price and detail
      this.regularPrice = item.price || 0;
      this.regularDetail = item.detail || '';
    },
    
    // Exit card edit mode
    exitCardEditMode() {
      this.isEditingInCard = false;
      this.editingItemId = null;
      this.originalCardData = null;
      this.clearRegularForm();
    },
    
    // Cancel card edit mode and restore original data
    cancelCardEdit() {
      if (this.originalCardData) {
        this.regularToothSurfaces = [...this.originalCardData.regularToothSurfaces];
        this.regularDiagnosis = this.originalCardData.regularDiagnosis;
        this.regularTreatment = this.originalCardData.regularTreatment;
        this.regularPrice = this.originalCardData.regularPrice;
        this.regularDetail = this.originalCardData.regularDetail;
        this.teethSelected = [...this.originalCardData.teethSelected];
      }
      this.exitCardEditMode();
    },
    
    // Save card edit changes
    saveCardEdit() {
      // Validate form
      this.$v.regularDiagnosis.$touch();
      this.$v.regularTreatment.$touch();
      if (this.$v.$anyError) return;

      // Validate that at least one tooth is selected
      if (!this.teethSelected.length) {
        return;
      }

      // Find the item being edited
      const editingIndex = this.treatmentPlan.findIndex(item => item.id === this.editingItemId);
      if (editingIndex === -1) return;

      // In edit mode, we only update the single existing row (use first selected tooth)
      // If user selected multiple teeth, use only the first one

      // Create updated treatment data using only the first selected tooth
      const updatedTreatment = {
        id: this.editingItemId,
        toothNo: this.teethSelected[0].id,
        toothSurfaces: this.regularToothSurfaces.map(s => parseInt(s.id || s, 10)),
        diagnosis: this.regularDiagnosis.id,
        treatment: this.regularTreatment.id,
        price: this.regularPrice || 0,
        detail: this.regularDetail,
        status: 'Planning',
        aiGenerated: false,
        _showDetails: Boolean(this.regularDetail)
      };

      // Update the treatment plan
      this.$set(this.treatmentPlan, editingIndex, updatedTreatment);

      // Exit edit mode
      this.exitCardEditMode();
    },
    
    // Modal control
    async show(patient) {
      this.patient = patient;
      this.patientId = patient.id;

      // Initialize ProtocolMedia workflow
      this.initializeWorkflow({
        treatmentId: this.generateTreatmentId(),
        patient: patient,
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl
      });

      this.resetWorkflow();
      this.resetProtocolMedia(); // Ensure clean state when opening modal

      // Fetch all patient data (photos + treatment plans)
      await this.fetchAllPatientData();
      

      this.$refs.EnhancedTreatmentPlan.show();
    },

    // Comprehensive data fetching using Vuex store actions
    async fetchAllPatientData() {
      const patientId = this.patientId || this.patient?.id;
      if (!patientId) return;

      try {
        this.isLoadingExisting = true;

        const clinicUrl = this.$route.params.clinicUrl;
        const branchUrl = this.$route.params.branchUrl;

        // Use store actions instead of direct API calls
        await Promise.all([
          // Fetch protocol media via store
          this.$store.dispatch('protocolMedia/refreshProtocolMedia'),
          // Fetch treatment plans via store
          this.$store.dispatch('moduleTreatmentPlan/fetchTreatmentPlans', {
            clinicUrl,
            branchUrl,
            patientId
          })
        ]);

        // Get data from store state
        const photos = this.$store.state.protocolMedia.protocolMedia;
        const treatmentPlans = this.$store.state.moduleTreatmentPlan.planList;

        // Process photos
        if (photos && photos.length > 0) {
          this.processAndSetPhotos(photos);
        }

        // Process treatment plans
        if (treatmentPlans && treatmentPlans.length > 0) {
          this.processAndSetTreatmentPlan(treatmentPlans);
        }

        // Determine which step to show based on available data
        this.determineInitialStep();

      } catch (error) {
        console.error('Failed to fetch patient data:', error);
        // Start from step 1 on error
        this.$store.commit('dentalAI/SET_CURRENT_STEP', 1);
        this.stepsCompleted = [false, false];
      } finally {
        this.isLoadingExisting = false;
      }
    },

    // Process and set photos
    processAndSetPhotos(mediaFiles) {
      // Group by view type and get most recent
      const groupedMedia = {
        upper: null,
        lower: null
      };

      mediaFiles.forEach(media => {
        // Check metadata.view_arch first (most reliable)
        let viewType = null;
        if (media.metadata && media.metadata.view_arch) {
          viewType = media.metadata.view_arch;
        } else if (media.protocol_info && media.protocol_info.view_details && media.protocol_info.view_details.arch) {
          viewType = media.protocol_info.view_details.arch;
        }

        if (viewType && (!groupedMedia[viewType] || new Date(media.creationDt) > new Date(groupedMedia[viewType].creationDt))) {
          groupedMedia[viewType] = media;
        }
      });

      // Set photos in protocolMedia store (which will update the computed properties automatically)
      if (groupedMedia.upper) {
        // Store in protocolMedia store - this will update upperPhotoPreview automatically
        this.$store.commit('protocolMedia/SET_LOCAL_PHOTO', {
          type: 'upper',
          photoData: {
            file: null,
            base64: groupedMedia.upper.mediaURL,
            uploaded: true,
            uploadProgress: 100,
            metadata: groupedMedia.upper.metadata || null,
            capturedAt: groupedMedia.upper.creationDt
          }
        });
      }

      if (groupedMedia.lower) {
        // Store in protocolMedia store - this will update lowerPhotoPreview automatically
        this.$store.commit('protocolMedia/SET_LOCAL_PHOTO', {
          type: 'lower',
          photoData: {
            file: null,
            base64: groupedMedia.lower.mediaURL,
            uploaded: true,
            uploadProgress: 100,
            metadata: groupedMedia.lower.metadata || null,
            capturedAt: groupedMedia.lower.creationDt
          }
        });
      }

      this.loadedPhotosCount = (groupedMedia.upper ? 1 : 0) + (groupedMedia.lower ? 1 : 0);
    },

    // Process and set treatment plan
    processAndSetTreatmentPlan(treatmentPlans) {
      // Find active treatment plan or the most recent one
      let activePlan = treatmentPlans.find(plan => plan.status === 'Active');

      if (!activePlan && treatmentPlans.length > 0) {
        // If no active plan, use the most recent one
        activePlan = treatmentPlans.sort((a, b) =>
          new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt)
        )[0];
      }

      if (activePlan && activePlan.treatmentPlanItems && activePlan.treatmentPlanItems.length > 0) {
        // Set the treatment plan items
        this.treatmentPlan = activePlan.treatmentPlanItems.map(item => ({
          ...item,
          aiGenerated: false, // Mark as existing, not AI generated
          // Ensure required fields are present
          tooth: item.tooth || item.toothNo,
          toothNo: item.toothNo || item.tooth,
          price: item.price || 0
        }));

        // Store full plan object (like TreatmentPlan.vue)
        this.planSelected = activePlan;

        // Also set it in the store for other components to use
        this.$store.commit('dentalAI/SET_AUTO_GENERATED_PLAN', this.treatmentPlan);
      } else {
        this.planSelected = {}; // Reset when no plan found
      }
    },

    // Determine initial step based on available data
    determineInitialStep() {
      const hasPhotos = this.upperPhotoPreview || this.lowerPhotoPreview;
      const hasTreatmentPlan = this.treatmentPlan.length > 0;

      if (hasPhotos) {
        // Have photos - go directly to treatment plan (step 2)
        this.$store.commit('dentalAI/SET_CURRENT_STEP', 2);
        this.stepsCompleted = [true, false];

        // Have photos - start at step 2 (treatment plan)
      } else {
        // No photos - start from beginning (step 1)
        this.$store.commit('dentalAI/SET_CURRENT_STEP', 1);
        this.stepsCompleted = [false, false];
      }

      // Store update already handled above for each case
    },
    
    // Generate treatment ID for protocol media
    generateTreatmentId() {
      // Generate a temporary treatment ID for the session
      return `enhanced_treatment_${this.patientId}_${Date.now()}`;
    },
    
    closeModal() {
      this.$refs.EnhancedTreatmentPlan.hide();
    },
    
    handleModalHidden() {
      this.disableNavigationLock(); // Disable navigation lock when modal is hidden
      this.resetWorkflow();
      this.resetProtocolMedia(); // Clear photo data from protocolMedia store
      this.exitCardEditMode(); // Reset editing mode states
      this.treatmentPlan = [];
      this.planSelected = {}; // Reset plan object (like TreatmentPlan.vue)
      this.patient = {};
      this.patientId = null;

      // Reset photo transformations
      this.upperPhotoTransformations = { rotation: 0, flipHorizontal: false, flipVertical: false, cropArea: null };
      this.lowerPhotoTransformations = { rotation: 0, flipHorizontal: false, flipVertical: false, cropArea: null };
    },
    
    // Step navigation
    nextStep() {
      this.nextStepAction();
    },
    
    previousStep() {
      this.previousStepAction();
    },

    // Direct navigation to Step 1 for photo re-upload
    goToStep1() {
      // Set current step to 1
      this.$store.commit('dentalAI/SET_CURRENT_STEP', 1);
      // Update step completion states - only Step 1 will be active
      this.stepsCompleted = [false, false];

      // Reset photo transformations when going back to photo capture
      this.upperPhotoTransformations = { rotation: 0, flipHorizontal: false, flipVertical: false, cropArea: null };
      this.lowerPhotoTransformations = { rotation: 0, flipHorizontal: false, flipVertical: false, cropArea: null };
    },

    // Step 1: Simple Camera Modal handlers
    openCamera(type) {
      this.currentCameraType = type;
      this.resetCameraState();
      this.$refs.cameraModal.show();
      this.$nextTick(() => {
        this.initCamera();
      });
    },
    
    closeCamera() {
      this.stopCamera();
      this.$refs.cameraModal.hide();
      this.resetCameraState();
    },
    
    resetCameraState() {
      this.cameraReady = false;
      this.cameraError = null;
      this.photoTakenInModal = false;
      this.capturedPhotoUrlInModal = null;
      this.switchingCamera = false;
      // Reset to default back camera
      this.currentFacingMode = 'environment';
      this.availableCameras = [];
    },
    
    async initCamera() {
      try {
        this.cameraError = null;

        // Detect available cameras
        await this.detectAvailableCameras();

        const constraints = {
          video: {
            facingMode: this.currentFacingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };

        this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

        if (this.$refs.videoPreview) {
          this.$refs.videoPreview.srcObject = this.mediaStream;
          this.cameraReady = true;
        }
      } catch (error) {
        console.error('Camera initialization failed:', error);

        // If the requested camera fails, try the other one
        if (this.currentFacingMode === 'user') {
          this.currentFacingMode = 'environment';
          return this.initCamera();
        } else if (this.currentFacingMode === 'environment') {
          this.currentFacingMode = 'user';
          return this.initCamera();
        }

        this.cameraError = 'ไม่สามารถเปิดกล้องได้ กรุณาตรวจสอบสิทธิ์การใช้กล้อง';
      }
    },

    async detectAvailableCameras() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        this.availableCameras = devices.filter(device => device.kind === 'videoinput');
      } catch (error) {
        console.error('Failed to detect cameras:', error);
        this.availableCameras = [];
      }
    },

    async switchCamera() {
      if (this.switchingCamera || this.availableCameras.length < 2) {
        return;
      }

      try {
        this.switchingCamera = true;

        // Stop current camera
        this.stopCamera();

        // Toggle facing mode
        this.currentFacingMode = this.currentFacingMode === 'environment' ? 'user' : 'environment';

        // Reinitialize camera with new facing mode
        await this.initCamera();

      } catch (error) {
        console.error('Camera switch failed:', error);
        // If switch fails, try to go back to the previous mode
        this.currentFacingMode = this.currentFacingMode === 'environment' ? 'user' : 'environment';
        await this.initCamera();
      } finally {
        this.switchingCamera = false;
      }
    },

    stopCamera() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
        this.mediaStream = null;
      }
      this.cameraReady = false;
    },
    
    capturePhotoInModal() {
      if (!this.$refs.videoPreview || !this.cameraReady) {
        return;
      }
      
      try {
        const video = this.$refs.videoPreview;
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        
        // Set canvas dimensions to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // Draw video frame to canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert to base64
        this.capturedPhotoUrlInModal = canvas.toDataURL('image/jpeg', 0.9);
        this.photoTakenInModal = true;
        
      } catch (error) {
        console.error('Photo capture failed:', error);
        this.cameraError = 'การถ่ายภาพล้มเหลว กรุณาลองใหม่';
      }
    },
    
    retakePhoto() {
      this.photoTakenInModal = false;
      this.capturedPhotoUrlInModal = null;
      this.initCamera();
    },
    
    async acceptPhoto() {
      if (!this.capturedPhotoUrlInModal || !this.currentCameraType) {
        return;
      }
      
      try {
        // Detect and store image dimensions
        await this.detectImageDimensions(this.capturedPhotoUrlInModal, this.currentCameraType);
        
        // Convert base64 to File for ProtocolMedia
        const file = this.base64ToFile(this.capturedPhotoUrlInModal, `${this.currentCameraType}_capture.jpg`);
        
        // Save to ProtocolMedia store
        await this.saveLocalPhoto({
          type: this.currentCameraType,
          file: file,
          base64: this.capturedPhotoUrlInModal
        });

        // Reset transformations when new photo is captured
        if (this.currentCameraType === 'upper') {
          this.upperPhotoTransformations = { rotation: 0, flipHorizontal: false, flipVertical: false, cropArea: null };
        } else {
          this.lowerPhotoTransformations = { rotation: 0, flipHorizontal: false, flipVertical: false, cropArea: null };
        }

        // Wait for store to update
        await this.$nextTick();

        // Run quality check
        const qualityPassed = await this.checkImageQuality(this.capturedPhotoUrlInModal, this.currentCameraType);

        this.closeCamera();

        if (!qualityPassed) {
          // Quality check failed, photo was deleted
          // Re-open camera for retake
          this.showCameraModal(this.currentCameraType);
          return;
        }

      } catch (error) {
        console.error('Failed to save photo:', error);
        this.cameraError = 'การบันทึกภาพล้มเหลว กรุณาลองใหม่';
      }
    },
    
    // File Upload Methods
    openFileUpload(type) {
      this.currentUploadType = type;

      // Use the existing file input element instead of creating a new one
      const fileInput = this.$refs.fileInput;
      if (!fileInput) {
        console.error('File input element not found');
        return;
      }

      // Reset the file input value to allow re-selecting the same file
      fileInput.value = '';

      // Trigger click on the existing file input
      fileInput.click();
    },

    handleFileSelect(event) {
      const files = event.target.files;
      const type = this.currentUploadType;

      if (!type) {
        console.error('Upload type not set');
        this.$refs.photoOptionsModal.show();
        return;
      }

      if (files && files.length > 0) {
        this.processUploadedFile(files[0], type);
        // Clear the input value after processing to allow re-selection
        event.target.value = '';
        // Reset the upload type after use
        this.currentUploadType = null;
      }
    },
    
    async processUploadedFile(file, type) {
      // Validate file
      const validationResult = this.validateImageFile(file);
      if (!validationResult.isValid) {
        console.error('File validation failed:', validationResult.message);
        alert(validationResult.message);
        this.$refs.photoOptionsModal.show();
        return;
      }

      try {
        // Convert file to base64
        const base64Data = await this.fileToBase64(file);

        // Detect and store image dimensions
        await this.detectImageDimensions(base64Data, type);

        // Save to ProtocolMedia store
        await this.saveLocalPhoto({
          type: type,
          file: file,
          base64: base64Data
        });

        // Reset transformations when new photo is uploaded
        if (type === 'upper') {
          this.upperPhotoTransformations = { rotation: 0, flipHorizontal: false, flipVertical: false, cropArea: null };
        } else {
          this.lowerPhotoTransformations = { rotation: 0, flipHorizontal: false, flipVertical: false, cropArea: null };
        }

        // Verify photo was saved - add small delay to ensure store is updated
        await this.$nextTick();
        const photoPreview = type === 'upper' ? this.upperPhotoPreview : this.lowerPhotoPreview;
        if (photoPreview) {
          // Run quality check
          const qualityPassed = await this.checkImageQuality(base64Data, type);
          if (!qualityPassed) {
            // Quality check failed, photo was deleted
            this.$refs.photoOptionsModal.show();
            return;
          }
        } else {
          console.error(`⚠️ ${type} photo not showing in preview after save`);
        }

      } catch (error) {
        console.error('File upload failed:', error);
        alert('การอัปโหลดภาพล้มเหลว กรุณาลองใหม่');
        this.$refs.photoOptionsModal.show();
      }
    },
    
    validateImageFile(file) {
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
      if (!validTypes.includes(file.type)) {
        return {
          isValid: false,
          message: 'รองรับเฉพาะไฟล์ JPG, PNG, WebP และ HEIC เท่านั้น'
        };
      }
      
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        return {
          isValid: false,
          message: 'ไฟล์ใหญ่เกินไป กรุณาเลือกไฟล์ที่มีขนาดไม่เกิน 10MB'
        };
      }
      
      return { isValid: true };
    },

    async checkImageQuality(base64Image, type) {
      this.qualityCheckInProgress = true;

      try {
        const qualityResult = await analyzeImageQuality(base64Image);

        // Store quality result
        if (type === 'upper') {
          this.upperPhotoQuality = qualityResult;
        } else {
          this.lowerPhotoQuality = qualityResult;
        }

        // Check if quality is below threshold (must retake)
        if (!qualityResult.passingScore) {
          console.warn(`${type} photo quality is below threshold:`, qualityResult.score);

          // Show error alert with recommendations
          const recommendationsText = qualityResult.recommendations.join('\n');
          alert(`คุณภาพภาพต่ำเกินไป (${type === 'upper' ? 'ฟันบน' : 'ฟันล่าง'})\n\nปัญหา:\n${recommendationsText}\n\nกรุณาถ่ายภาพใหม่`);

          // Delete the photo and force retake
          await this.deletePhoto(type);

          return false;
        }

        return true;

      } catch (error) {
        console.error('Quality check failed:', error);
        // On error, allow photo but show warning
        alert('ไม่สามารถตรวจสอบคุณภาพภาพได้ กรุณาตรวจสอบภาพด้วยตาเปล่า');
        return true;
      } finally {
        this.qualityCheckInProgress = false;
      }
    },

    getQualityIcon(score) {
      return getQualityIcon(score);
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
    },
    
    // Convert base64 to File object
    base64ToFile(base64String, filename) {
      const arr = base64String.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      
      return new File([u8arr], filename, { type: mime });
    },
    
    // Detect and store image dimensions for proper overlay positioning
    detectImageDimensions(base64Data, type) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          this.imageDimensions[type] = {
            width: img.width,
            height: img.height
          };
          resolve();
        };
        img.onerror = () => {
          console.warn(`Failed to detect dimensions for ${type}, using defaults`);
          resolve();
        };
        img.src = base64Data;
      });
    },
    
    // Drag & Drop Methods
    dragEnter(type) {
      this.dragActive[type] = true;
    },
    
    dragLeave(type) {
      this.dragActive[type] = false;
    },
    
    handleFileDrop(event, type) {
      this.dragActive[type] = false;
      
      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        this.processUploadedFile(files[0], type);
      }
    },
    
    // Photo Management Methods
    replacePhoto(type) {
      // Reset transformations for the photo being replaced
      if (type === 'upper') {
        this.upperPhotoTransformations = { rotation: 0, flipHorizontal: false, flipVertical: false, cropArea: null };
        this.upperPhotoQuality = null;
      } else {
        this.lowerPhotoTransformations = { rotation: 0, flipHorizontal: false, flipVertical: false, cropArea: null };
        this.lowerPhotoQuality = null;
      }

      // Show options to replace photo - use mobile-friendly approach
      if (this.isMobileDevice) {
        this.openFileUpload(type);
      } else {
        this.showPhotoOptions(type);
      }
    },
    
    deletePhoto(type) {
      const photoName = type === 'upper' ? 'ฟันบน' : 'ฟันล่าง';

      if (confirm(`ต้องการลบภาพ${photoName}?`)) {
        // Clear from ProtocolMedia store
        this.clearLocalPhoto(type);
        // Reset transformations and quality data
        if (type === 'upper') {
          this.upperPhotoTransformations = { rotation: 0, flipHorizontal: false, flipVertical: false, cropArea: null };
          this.upperPhotoQuality = null;
        } else {
          this.lowerPhotoTransformations = { rotation: 0, flipHorizontal: false, flipVertical: false, cropArea: null };
          this.lowerPhotoQuality = null;
        }
      }
    },

    // Image transformation methods
    getTransformStyle(type) {
      const transformations = type === 'upper' ? this.upperPhotoTransformations : this.lowerPhotoTransformations;
      const { rotation, flipHorizontal, flipVertical } = transformations;

      let transform = '';

      if (rotation > 0) {
        transform += `rotate(${rotation}deg) `;
      }

      if (flipHorizontal) {
        transform += 'scaleX(-1) ';
      }

      if (flipVertical) {
        transform += 'scaleY(-1) ';
      }

      return transform ? { transform: transform.trim() } : {};
    },

    async rotatePhoto(type) {
      const preview = type === 'upper' ? this.upperPhotoPreview : this.lowerPhotoPreview;
      const transformations = type === 'upper' ? this.upperPhotoTransformations : this.lowerPhotoTransformations;
      const localPhoto = this.$store.state.protocolMedia.localPhotos[type];

      // Calculate new rotation
      const newRotation = (transformations.rotation + 90) % 360;

      // Apply transformation to the actual image data
      const transformedImage = await applyImageTransformations(preview, {
        rotation: newRotation,
        flipHorizontal: transformations.flipHorizontal,
        flipVertical: transformations.flipVertical
      });

      // Convert base64 to file if needed
      const transformedFile = localPhoto.file ? base64ToFile(transformedImage, localPhoto.file.name) : null;

      // Update the store with transformed image
      this.$store.commit('protocolMedia/SET_LOCAL_PHOTO', {
        type,
        photoData: {
          file: transformedFile,
          base64: transformedImage
        }
      });

      // Reset transformations since the image is now physically transformed
      transformations.rotation = 0;
      transformations.flipHorizontal = false;
      transformations.flipVertical = false;
    },

    async flipPhotoHorizontal(type) {
      const preview = type === 'upper' ? this.upperPhotoPreview : this.lowerPhotoPreview;
      const transformations = type === 'upper' ? this.upperPhotoTransformations : this.lowerPhotoTransformations;
      const localPhoto = this.$store.state.protocolMedia.localPhotos[type];

      // Apply transformation to the actual image data
      const transformedImage = await applyImageTransformations(preview, {
        rotation: transformations.rotation,
        flipHorizontal: !transformations.flipHorizontal,
        flipVertical: transformations.flipVertical
      });

      // Convert base64 to file if needed
      const transformedFile = localPhoto.file ? base64ToFile(transformedImage, localPhoto.file.name) : null;

      // Update the store with transformed image
      this.$store.commit('protocolMedia/SET_LOCAL_PHOTO', {
        type,
        photoData: {
          file: transformedFile,
          base64: transformedImage
        }
      });

      // Reset transformations since the image is now physically transformed
      transformations.rotation = 0;
      transformations.flipHorizontal = false;
      transformations.flipVertical = false;
    },

    async flipPhotoVertical(type) {
      const preview = type === 'upper' ? this.upperPhotoPreview : this.lowerPhotoPreview;
      const transformations = type === 'upper' ? this.upperPhotoTransformations : this.lowerPhotoTransformations;
      const localPhoto = this.$store.state.protocolMedia.localPhotos[type];

      // Apply transformation to the actual image data
      const transformedImage = await applyImageTransformations(preview, {
        rotation: transformations.rotation,
        flipHorizontal: transformations.flipHorizontal,
        flipVertical: !transformations.flipVertical
      });

      // Convert base64 to file if needed
      const transformedFile = localPhoto.file ? base64ToFile(transformedImage, localPhoto.file.name) : null;

      // Update the store with transformed image
      this.$store.commit('protocolMedia/SET_LOCAL_PHOTO', {
        type,
        photoData: {
          file: transformedFile,
          base64: transformedImage
        }
      });

      // Reset transformations since the image is now physically transformed
      transformations.rotation = 0;
      transformations.flipHorizontal = false;
      transformations.flipVertical = false;
    },

    // Crop mode methods
    enterCropMode(type) {
      this.cropMode.active = true;
      this.cropMode.type = type;
      // Reset to default crop area
      this.cropMode.cropArea = { x: 10, y: 10, width: 80, height: 80 };

      // Add event listeners for mouse/touch events
      document.addEventListener('mousemove', this.handleCropDrag, { passive: false });
      document.addEventListener('mouseup', this.endCropDrag, { passive: false });
      document.addEventListener('touchmove', this.handleCropDrag, { passive: false });
      document.addEventListener('touchend', this.endCropDrag, { passive: false });
    },

    cancelCrop() {
      this.cropMode.active = false;
      this.cropMode.type = null;
      this.cropMode.isDragging = false;
      this.cropMode.isResizing = false;

      // Remove event listeners
      document.removeEventListener('mousemove', this.handleCropDrag, { passive: false });
      document.removeEventListener('mouseup', this.endCropDrag, { passive: false });
      document.removeEventListener('touchmove', this.handleCropDrag, { passive: false });
      document.removeEventListener('touchend', this.endCropDrag, { passive: false });
    },

    getCropRectangleStyle() {
      const { x, y, width, height } = this.cropMode.cropArea;

      // Get container and image to calculate offset
      const containerRef = this.cropMode.type === 'upper' ? this.$refs.upperPhotoContainer : this.$refs.lowerPhotoContainer;
      if (!containerRef) {
        return {
          left: `${x}%`,
          top: `${y}%`,
          width: `${width}%`,
          height: `${height}%`
        };
      }

      const imgElement = containerRef.querySelector('img');
      if (!imgElement) {
        return {
          left: `${x}%`,
          top: `${y}%`,
          width: `${width}%`,
          height: `${height}%`
        };
      }

      // Get dimensions
      const containerRect = containerRef.getBoundingClientRect();
      const imgRect = imgElement.getBoundingClientRect();

      // Calculate offset of image within container (due to object-fit: contain centering)
      const offsetX = ((imgRect.left - containerRect.left) / containerRect.width) * 100;
      const offsetY = ((imgRect.top - containerRect.top) / containerRect.height) * 100;

      // Calculate image size as percentage of container
      const imgWidthPercent = (imgRect.width / containerRect.width) * 100;
      const imgHeightPercent = (imgRect.height / containerRect.height) * 100;

      // Convert crop position from image-relative to container-relative
      return {
        left: `${offsetX + (x * imgWidthPercent / 100)}%`,
        top: `${offsetY + (y * imgHeightPercent / 100)}%`,
        width: `${(width * imgWidthPercent / 100)}%`,
        height: `${(height * imgHeightPercent / 100)}%`
      };
    },

    startDragCrop(event) {
      event.preventDefault();
      this.cropMode.isDragging = true;

      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;

      this.cropMode.dragStart = { x: clientX, y: clientY };
    },

    startResize(handle, event) {
      event.preventDefault();
      event.stopPropagation();
      this.cropMode.isResizing = true;
      this.cropMode.resizeHandle = handle;

      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;

      this.cropMode.dragStart = { x: clientX, y: clientY };
    },

    handleCropDrag(event) {
      if (!this.cropMode.isDragging && !this.cropMode.isResizing) return;

      event.preventDefault();

      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;

      // Get container and image elements
      const containerRef = this.cropMode.type === 'upper' ? this.$refs.upperPhotoContainer : this.$refs.lowerPhotoContainer;
      if (!containerRef) return;

      // Get the actual image element (not the container)
      const imgElement = containerRef.querySelector('img');
      if (!imgElement) return;

      // Get image's rendered dimensions (accounts for object-fit: contain)
      // This is the ACTUAL visible image size, excluding gray areas
      const imgRect = imgElement.getBoundingClientRect();

      // Calculate delta based on IMAGE dimensions, not container
      // This ensures crop percentages are relative to the actual image
      const deltaX = ((clientX - this.cropMode.dragStart.x) / imgRect.width) * 100;
      const deltaY = ((clientY - this.cropMode.dragStart.y) / imgRect.height) * 100;

      if (this.cropMode.isDragging) {
        // Move the crop area
        let newX = this.cropMode.cropArea.x + deltaX;
        let newY = this.cropMode.cropArea.y + deltaY;

        // Constrain to bounds
        newX = Math.max(0, Math.min(newX, 100 - this.cropMode.cropArea.width));
        newY = Math.max(0, Math.min(newY, 100 - this.cropMode.cropArea.height));

        this.cropMode.cropArea.x = newX;
        this.cropMode.cropArea.y = newY;
      } else if (this.cropMode.isResizing) {
        // Resize the crop area
        const handle = this.cropMode.resizeHandle;
        const minSize = 10; // Minimum 10% size

        let { x, y, width, height } = this.cropMode.cropArea;

        if (handle.includes('n')) {
          const newY = y + deltaY;
          const newHeight = height - deltaY;
          if (newHeight >= minSize && newY >= 0) {
            y = newY;
            height = newHeight;
          }
        }
        if (handle.includes('s')) {
          const newHeight = height + deltaY;
          if (newHeight >= minSize && y + newHeight <= 100) {
            height = newHeight;
          }
        }
        if (handle.includes('w')) {
          const newX = x + deltaX;
          const newWidth = width - deltaX;
          if (newWidth >= minSize && newX >= 0) {
            x = newX;
            width = newWidth;
          }
        }
        if (handle.includes('e')) {
          const newWidth = width + deltaX;
          if (newWidth >= minSize && x + newWidth <= 100) {
            width = newWidth;
          }
        }

        this.cropMode.cropArea = { x, y, width, height };
      }

      this.cropMode.dragStart = { x: clientX, y: clientY };
    },

    endCropDrag() {
      this.cropMode.isDragging = false;
      this.cropMode.isResizing = false;
      this.cropMode.resizeHandle = null;
    },

    async applyCropToPhoto(type) {
      try {
        // Get the current photo preview
        const photoPreview = type === 'upper' ? this.upperPhotoPreview : this.lowerPhotoPreview;
        if (!photoPreview) return;

        // Apply crop directly using cropArea values
        // No need to swap width/height here because:
        // 1. handleCropDrag() already calculates correct percentages (with effectiveWidth/Height)
        // 2. The actual image is already rotated with correct dimensions
        // 3. We only swap for visual display in getCropRectangleStyle()
        const croppedImage = await applyCrop(photoPreview, this.cropMode.cropArea);

        // Convert to file
        const file = this.base64ToFile(croppedImage, `${type}_cropped.jpg`);

        // Save the cropped image
        await this.saveLocalPhoto({
          type: type,
          file: file,
          base64: croppedImage
        });

        // Store the crop area in transformations (for reference)
        const transformations = type === 'upper' ? this.upperPhotoTransformations : this.lowerPhotoTransformations;
        transformations.cropArea = { ...this.cropMode.cropArea };

        // Exit crop mode
        this.cancelCrop();

        // Show success message
        this.$bvToast.toast('ครอปภาพเรียบร้อยแล้ว', {
          title: 'สำเร็จ',
          variant: 'success',
          solid: true,
          autoHideDelay: 2000
        });
      } catch (error) {
        console.error('Failed to apply crop:', error);
        this.$bvToast.toast('การครอปภาพล้มเหลว กรุณาลองใหม่', {
          title: 'ข้อผิดพลาด',
          variant: 'danger',
          solid: true
        });
      }
    },

    // Coordinate transformation methods for bounding box overlay
    getInverseTransformation(photoType) {
      const transformations = photoType === 'upper' ? this.upperPhotoTransformations : this.lowerPhotoTransformations;
      const { rotation, flipHorizontal, flipVertical } = transformations;

      // Calculate inverse transformation
      return {
        rotation: rotation > 0 ? 360 - rotation : 0, // Inverse rotation
        flipHorizontal: flipHorizontal, // Flip operations are their own inverse
        flipVertical: flipVertical
      };
    },

    transformCoordinates(coords, width, height, transformations) {
      let [x1, y1, x2, y2] = coords;
      const { rotation, flipHorizontal, flipVertical } = transformations;

      // Apply inverse transformations in reverse order

      // 1. Inverse flip operations
      if (flipHorizontal) {
        const newX1 = width - x2;
        const newX2 = width - x1;
        x1 = newX1;
        x2 = newX2;
      }

      if (flipVertical) {
        const newY1 = height - y2;
        const newY2 = height - y1;
        y1 = newY1;
        y2 = newY2;
      }

      // 2. Inverse rotation
      if (rotation > 0) {
        // For each rotation step (90, 180, 270), apply inverse
        let steps = rotation / 90;

        for (let i = 0; i < steps; i++) {
          // Rotate coordinates 90 degrees counter-clockwise (inverse of 90 clockwise)
          // For 90° counter-clockwise: (x,y) → (y, width-x)
          const newX1 = y1;
          const newY1 = width - x1;  // ✅ ใช้ width แทน height
          const newX2 = y2;
          const newY2 = width - x2;  // ✅ ใช้ width แทน height

          x1 = newX1;
          y1 = newY1;
          x2 = newX2;
          y2 = newY2;

          // Swap width and height for next iteration
          [width, height] = [height, width];
        }
      }

      return [Math.min(x1, x2), Math.min(y1, y2), Math.max(x1, x2), Math.max(y1, y2)];
    },

    async applyTransformationsBeforeUpload() {
      // Apply transformations to upper photo if it exists
      if (this.upperPhotoPreview) {
        const hasUpperTransforms = this.upperPhotoTransformations.rotation > 0 ||
          this.upperPhotoTransformations.flipHorizontal ||
          this.upperPhotoTransformations.flipVertical;

        if (hasUpperTransforms) {
          const transformedImage = await applyImageTransformations(
            this.upperPhotoPreview,
            this.upperPhotoTransformations
          );

          // Convert transformed image back to File object
          const transformedFile = base64ToFile(transformedImage, 'upper-occlusal.jpg');

          // Update the local photo in the store
          await this.saveLocalPhoto({
            type: 'upper',
            file: transformedFile,
            base64: transformedImage
          });
        }
      }

      // Apply transformations to lower photo if it exists
      if (this.lowerPhotoPreview) {
        const hasLowerTransforms = this.lowerPhotoTransformations.rotation > 0 ||
          this.lowerPhotoTransformations.flipHorizontal ||
          this.lowerPhotoTransformations.flipVertical;

        if (hasLowerTransforms) {
          const transformedImage = await applyImageTransformations(
            this.lowerPhotoPreview,
            this.lowerPhotoTransformations
          );

          // Convert transformed image back to File object
          const transformedFile = base64ToFile(transformedImage, 'lower-occlusal.jpg');

          // Update the local photo in the store
          await this.saveLocalPhoto({
            type: 'lower',
            file: transformedFile,
            base64: transformedImage
          });
        }
      }
    },
    
    // Photo Options Methods
    showPhotoOptions(type) {
      this.currentUploadType = type;

      // Use same behavior as replacePhoto - mobile gets OS camera/gallery picker, desktop gets options modal
      if (this.isMobileDevice) {
        this.openFileUpload(type);
      } else {
        this.$refs.photoOptionsModal.show();
      }
    },
    
    selectCameraOption() {
      const uploadType = this.currentUploadType;
      this.$refs.photoOptionsModal.hide();
      this.openCamera(uploadType);
    },
    
    selectUploadOption() {
      const uploadType = this.currentUploadType;
      this.$refs.photoOptionsModal.hide();
      this.openFileUpload(uploadType);
    },

    async proceedToAnalysis() {
      if (!this.hasLocalPhotos) {
        return;
      }

      // Check which photos are new (need quality check)
      const upperPhoto = this.$store.state.protocolMedia?.localPhotos?.upper;
      const lowerPhoto = this.$store.state.protocolMedia?.localPhotos?.lower;

      const hasNewUpperPhoto = !!upperPhoto?.file;
      const hasNewLowerPhoto = !!lowerPhoto?.file;

      // Validate photo quality ONLY for new photos (not existing S3 photos)
      let qualityCheckFailed = false;
      let message = '';

      if (hasNewUpperPhoto) {
        const upperQualityPass = this.upperPhotoQuality && this.upperPhotoQuality.passingScore;
        if (!upperQualityPass) {
          qualityCheckFailed = true;
          message += '❌ ฟันบน: ';
          if (this.upperPhotoQuality) {
            message += this.upperPhotoQuality.recommendations.join(', ');
          } else {
            message += 'ยังไม่ได้ตรวจสอบคุณภาพ';
          }
          message += '\n';
        }
      }

      if (hasNewLowerPhoto) {
        const lowerQualityPass = this.lowerPhotoQuality && this.lowerPhotoQuality.passingScore;
        if (!lowerQualityPass) {
          qualityCheckFailed = true;
          message += '❌ ฟันล่าง: ';
          if (this.lowerPhotoQuality) {
            message += this.lowerPhotoQuality.recommendations.join(', ');
          } else {
            message += 'ยังไม่ได้ตรวจสอบคุณภาพ';
          }
        }
      }

      if (qualityCheckFailed) {
        alert('คุณภาพภาพไม่เพียงพอ กรุณาถ่ายภาพใหม่:\n\n' + message);
        return;
      }

      this.isProcessing = true;

      try {
        // Step 1: Apply transformations to photos before upload
        await this.applyTransformationsBeforeUpload();

        // Step 2: Batch upload photos to ProtocolMedia API
        await this.batchUploadPhotos();

        // Step 3: Proceed directly to treatment plan step (skip AI analysis)
        this.nextStep();
        this.isProcessing = false;

        // Show info message
        this.$refs.Dialog.fire({
          title: 'บันทึกภาพสำเร็จ',
          text: 'คุณสามารถใช้ปุ่ม "วิเคราะห์ด้วย AI" เพื่อสร้างแผนการรักษาอัตโนมัติ',
          icon: 'success',
          timer: 3000
        });

      } catch (error) {
        console.error('Upload failed:', error);
        this.isProcessing = false;
        this.$refs.Dialog.fire({
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถอัปโหลดภาพได้ กรุณาลองใหม่อีกครั้ง',
          icon: 'error'
        });
      }
    },

    skipToTreatmentPlan() {
      // Jump to step 2 (treatment plan)
      this.nextStep(); // Step 1 → Step 2
    },

    async runAIAnalysis() {
      // Run AI analysis from Step 2 (optional)
      if (!this.upperPhotoPreview || !this.lowerPhotoPreview) {
        this.$refs.Dialog.fire({
          title: 'ไม่พบภาพถ่าย',
          text: 'กรุณากลับไปถ่ายภาพฟันบนและฟันล่าง',
          icon: 'warning'
        });
        return;
      }

      this.isAnalyzing = true;

      try {
        await this.analyzeDentalPhotos({
          patientId: this.patientId,
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          photoUrls: {
            upper: this.upperPhotoPreview,
            lower: this.lowerPhotoPreview
          }
        });

        // Auto-populate treatment plan from AI analysis
        if (this.autoGeneratedPlan && this.autoGeneratedPlan.length > 0) {
          // Check if we have existing plan
          if (this.planSelected?.id && this.treatmentPlan.length > 0) {
            // Show confirmation dialog
            const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
              'มีแผนการรักษาอยู่แล้ว',
              `มีแผนการรักษาเดิมอยู่แล้ว ${this.treatmentPlan.length} รายการ\n\n` +
              `AI ตรวจพบปัญหาใหม่ ${this.autoGeneratedPlan.length} รายการ\n\n` +
              `คุณต้องการดำเนินการอย่างไร?`,
              'warning',
              'เพิ่มรายการใหม่เข้าไป',
              'ใช้แผนเดิม'
            );

            if (isConfirmed) {
              // Append AI items to existing plan
              const aiItems = this.autoGeneratedPlan.map(item => ({
                ...item,
                isDiagnosisOnly: false,
                treatment: item.treatment || null,
                aiGenerated: true,
                isNew: true,
                toothSurfaces: item.toothSurfaces || [],
                needsTreatment: !item.treatment
              }));
              this.treatmentPlan = [...this.treatmentPlan, ...aiItems];
            }
          } else {
            // No existing plan - use AI results
            this.treatmentPlan = [...this.autoGeneratedPlan];
          }

          // Show success message
          this.$refs.Dialog.fire({
            title: 'วิเคราะห์สำเร็จ',
            text: `AI ตรวจพบปัญหา ${this.autoGeneratedPlan.length} จุด และสร้างแผนการรักษาแล้ว`,
            icon: 'success',
            timer: 3000
          });
        } else {
          // No problems detected
          this.$refs.Dialog.fire({
            title: 'ไม่พบปัญหา',
            text: 'AI ไม่พบปัญหาในภาพถ่ายฟัน คุณสามารถสร้างแผนการรักษาด้วยตัวเอง',
            icon: 'info'
          });
        }

        this.isAnalyzing = false;

      } catch (error) {
        console.error('AI Analysis failed:', error);
        this.isAnalyzing = false;
        this.$refs.Dialog.fire({
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถวิเคราะห์ด้วย AI ได้ กรุณาลองใหม่อีกครั้ง',
          icon: 'error'
        });
      }
    },

    async retryAnalysis() {
      // Retry the AI analysis
      try {
        await this.analyzeDentalPhotos({
          patientId: this.patientId,
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl
        });
        
        // Auto-populate treatment plan from AI analysis
        if (this.autoGeneratedPlan && this.autoGeneratedPlan.length > 0) {
          // Check if we have existing plan
          if (this.planSelected?.id && this.treatmentPlan.length > 0) {
            // Show confirmation dialog
            const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
              'มีแผนการรักษาอยู่แล้ว',
              `มีแผนการรักษาเดิมอยู่แล้ว ${this.treatmentPlan.length} รายการ\n\n` +
              `AI ตรวจพบปัญหาใหม่ ${this.autoGeneratedPlan.length} รายการ\n\n` +
              `คุณต้องการดำเนินการอย่างไร?`,
              'warning',
              'เพิ่มรายการใหม่เข้าไป',
              'ใช้แผนเดิม'
            );

            if (isConfirmed) {
              // Append AI items to existing plan
              // Transform diagnosis-only items to be editable treatment items
              const aiItems = this.autoGeneratedPlan.map(item => ({
                ...item,
                // Remove diagnosis-only flag to allow these items to be saved
                isDiagnosisOnly: false,
                // Keep treatment field as-is (may be null, user can add later)
                treatment: item.treatment || null,
                // Mark as AI-generated and new
                aiGenerated: true,
                isNew: true,
                // Ensure toothSurfaces is an array
                toothSurfaces: item.toothSurfaces || [],
                // Add flag to indicate if treatment is missing
                needsTreatment: !item.treatment
              }));
              this.treatmentPlan = [...this.treatmentPlan, ...aiItems];
            }
          } else {
            // No existing plan - use AI results
            this.treatmentPlan = [...this.autoGeneratedPlan];
          }

          // Auto-advance to Step 3 after successful analysis
          setTimeout(() => {
            this.nextStep();
          }, 1500);
        } else {
          // Even if no problems detected, advance to Step 3
          setTimeout(() => {
            this.nextStep();
          }, 1500);
        }
        
      } catch (error) {
        console.error('Retry analysis failed:', error);
      }
    },
    
    // AI Detection Visualization Methods
    getProblemsForPhoto(photoType) {
      if (!this.analysisResults || !this.analysisResults.problems) {
        return [];
      }
      
      return this.analysisResults.problems.filter(problem => 
        problem.photoType === photoType && problem.boundingBox
      );
    },
    
    getProblemOverlayStyle(problem) {
      if (!problem.boundingBox) {
        return {};
      }

      // Get photo type and transformations
      const photoType = problem.photoType || 'upper';
      const transformations = photoType === 'upper' ? this.upperPhotoTransformations : this.lowerPhotoTransformations;

      // Get image dimensions (adjusted for transformations if applied)
      const dimensions = this.imageDimensions[photoType];
      let displayWidth = problem.imageWidth || dimensions.width || 1920;
      let displayHeight = problem.imageHeight || dimensions.height || 1080;

      // Adjust dimensions if image is rotated 90° or 270°
      if (transformations.rotation === 90 || transformations.rotation === 270) {
        // Swap dimensions for rotated images
        displayWidth = problem.imageHeight || dimensions.height || 1080;
        displayHeight = problem.imageWidth || dimensions.width || 1920;
      }

      // Use AI coordinates directly (no transformation needed since we send transformed images to AI)
      const [x1, y1, x2, y2] = problem.boundingBox;

      // Convert to percentages for responsive overlay
      const leftPercent = (Math.min(x1, x2) / displayWidth) * 100;
      const topPercent = (Math.min(y1, y2) / displayHeight) * 100;
      const widthPercent = (Math.abs(x2 - x1) / displayWidth) * 100;
      const heightPercent = (Math.abs(y2 - y1) / displayHeight) * 100;
      
      const style = {
        position: 'absolute',
        left: `${leftPercent}%`,
        top: `${topPercent}%`,
        width: `${widthPercent}%`,
        height: `${heightPercent}%`,
        border: `2px solid ${this.getProblemColor(problem.confidence)}`,
        backgroundColor: `${this.getProblemColor(problem.confidence)}20`,
        borderRadius: '4px',
        pointerEvents: 'none',
        zIndex: 10
      };

      return style;
    },
    
    getProblemColor(confidence) {
      if (confidence >= 0.8) return '#dc3545'; // Red for high confidence
      if (confidence >= 0.6) return '#fd7e14'; // Orange for moderate confidence
      if (confidence >= 0.4) return '#ffc107'; // Yellow for mild confidence
      return '#6c757d'; // Gray for uncertain
    },
    
    getProblemBadgeVariant(confidence) {
      if (confidence >= 0.8) return 'danger';
      if (confidence >= 0.6) return 'warning';
      if (confidence >= 0.4) return 'info';
      return 'secondary';
    },
    
    // Step 3: Enhanced Treatment Plan Management
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedItems = [];
        this.allSelected = false;
      } else {
        this.selectedItems = this.treatmentPlan.map((item, index) => item.id || `temp-${index}`);
        this.allSelected = true;
      }
    },
    
    deleteSelected() {
      if (this.selectedItems.length === 0) return;
      
      const confirmMsg = `ต้องการลบ ${this.selectedItems.length} รายการที่เลือก?`;
      if (!confirm(confirmMsg)) return;
      
      // Remove selected items from treatmentPlan
      this.treatmentPlan = this.treatmentPlan.filter((item, index) => {
        const itemId = item.id || `temp-${index}`;
        return !this.selectedItems.includes(itemId);
      });
      
      // Clear selection
      this.selectedItems = [];
      this.allSelected = false;
      
    },
    
    
    quickAddTreatment(type) {
      const quickTemplates = {
        filling: {
          diagnosis: { id: 'K02.9', nameEn: 'Caries', code: 'K02.9' },
          treatment: { id: 'D2391', nameTh: 'อุดฟันด้วย Composite', code: 'D2391' },
          price: 2000
        },
        extraction: {
          diagnosis: { id: 'K04.0', nameEn: 'Pulpitis', code: 'K04.0' },
          treatment: { id: 'D7140', nameTh: 'ถอนฟัน', code: 'D7140' },
          price: 1500
        },
        cleaning: {
          diagnosis: { id: 'K05.1', nameEn: 'Gingivitis', code: 'K05.1' },
          treatment: { id: 'D1110', nameTh: 'ขูดหินปูน', code: 'D1110' },
          price: 1000
        },
        root_canal: {
          diagnosis: { id: 'K04.0', nameEn: 'Pulpitis', code: 'K04.0' },
          treatment: { id: 'D3310', nameTh: 'รักษารากฟัน', code: 'D3310' },
          price: 5000
        }
      };
      
      const template = quickTemplates[type];
      if (!template || this.teethSelected.length === 0) return;
      
      this.teethSelected.forEach(tooth => {
        this.treatmentPlan.push({
          id: `quick-${Date.now()}-${Math.random()}`,
          toothNo: tooth.id,
          toothSurfaces: [],
          diagnosis: template.diagnosis.id,
          treatment: template.treatment.id,
          price: template.price,
          detail: `Quick add: ${type}`,
          status: 'Planning',
          aiGenerated: false
        });
      });
      
      this.clearSelection();
      
    },
    
    // Helper methods for teeth interaction
    handleHoveringTooth() {
      // Handle tooth hovering - can be expanded for visual feedback
    },
    
    onShow() {
      // Tooth selection handled - triggers quick actions panel update
    },
    
    clearSelection() {
      this.teethSelected = [];
      this.selectedToothAISuggestion = null;
    },
    
    deleteRow(index) {
      this.treatmentPlan.splice(index, 1);
    },
    
    onDragEnd() {
      // Handle drag end
    },
    
    // Formatting helpers
    getToothName(toothNo) {
      const tooth = this.getToothBy(toothNo, 'id');
      return tooth ? tooth.name : toothNo;
    },
    
    formatSurfaces(surfaceIds) {
      if (!surfaceIds || surfaceIds.length === 0) return '-';
      return surfaceIds
        .map(id => {
          const surface = this.getToothSurfaceBy(id, 'id');
          return surface ? surface.name : '';
        })
        .filter(s => s)
        .join(', ');
    },
    
    formatDiagnosis(diagnosisId) {
      const diagnosis = this.getTreatDiagBy(diagnosisId, 'id');
      return diagnosis ? diagnosis.nameEn : diagnosisId;
    },
    
    formatTreatment(treatmentId) {
      const treatment = this.getTreatBy(treatmentId, 'id');
      return treatment ? treatment.nameTh : treatmentId;
    },
    
    getHealthText(health) {
      const translations = {
        'excellent': 'ดีมาก',
        'good': 'ดี',
        'fair': 'พอใช้',
        'poor': 'ต้องปรับปรุง'
      };
      return translations[health] || health;
    },
    
    // Save treatment plan
    async saveTreatmentPlan() {
      if (this.treatmentPlan.length === 0) {
        await this.$refs.Dialog.showAlertInfo(
          'กรุณาเพิ่มแผนการรักษา',
          'ต้องมีอย่างน้อย 1 รายการ',
          'warning'
        );
        return;
      }

      // Filter out diagnosis-only items and items without treatment
      const validTreatmentItems = this.treatmentPlan.filter(item => {
        // Remove diagnosis-only items (they should not be sent to API)
        if (item.isDiagnosisOnly === true) {
          return false;
        }

        // Remove items without valid treatment ID
        if (item.treatment === null || item.treatment === undefined) {
          return false;
        }

        return true;
      });

      // Validate that we have valid items to save
      if (validTreatmentItems.length === 0) {
        await this.$refs.Dialog.fire({
          title: 'มีรายการที่ยังไม่สมบูรณ์',
          html: 'กรุณาบันทึกซี่ฟัน และ การรักษา',
          icon: 'warning'
        });
        return;
      }

      const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
        'บันทึกแผนการรักษา?'
      );

      if (!isConfirmed) return;

      try {
        this.isLoading = true;

        const data = {
          patientId: this.patientId,
          status: 'Active',
          treatmentPlanItems: validTreatmentItems.map((item, index) => {
            // Remove client-side ID field to let backend generate proper integer IDs
            // Keep _clientId for internal tracking if needed
            const { id, _clientId, ...itemWithoutId } = item;
            return {
              ...itemWithoutId,
              toothSurfaces: item.toothSurfaces.map(surface =>
                typeof surface === 'string' ? parseInt(surface, 10) : surface
              ),
              status: 'Planning',
              order: index + 1,
              // If item has an existing backend ID (from loaded data), preserve it
              ...(typeof id === 'number' ? { id } : {})
            };
          })
        };

        // Use updateTreatmentPlan if editing existing plan, createTreatmentPlan for new plans
        if (this.planSelected?.id) {
          let { id } = this.planSelected;
          await this.updateTreatmentPlan({
            data: { id, ...data },
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl
          });
        } else {
          await this.createTreatmentPlan({
            data,
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl
          });
        }
        
        
        this.closeModal();
        
      } catch (error) {
        console.error('Failed to save treatment plan:', error);
      } finally {
        this.isLoading = false;
      }
    },
    
    // Regular Treatment Input Methods
    getRegularToothSurfaces(surfaces) {
      this.regularToothSurfaces = surfaces;
    },
    
    async focusRegularPriceInput() {
      await this.$nextTick();
      this.regularPrice = this.regularTreatment?.price || 0;
      this.$refs.regularTreatmentPriceInput?.focus();
    },
    
    onAddRegularTreatment() {
      this.$v.regularDiagnosis.$touch();
      this.$v.regularTreatment.$touch();
      if (this.$v.$anyError) return;
      
      this.regularPrice = this.regularPrice || 0;
      
      const items = this.formatRegularTreatmentItems();
      this.treatmentPlan.push(...items);
      this.clearRegularForm();
      
    },
    
    formatRegularTreatmentItems() {
      const items = [];
      this.teethSelected.forEach(tooth => {
        items.push({
          id: `regular-${Date.now()}-${Math.random()}`,
          toothNo: tooth.id,
          toothSurfaces: this.regularToothSurfaces.map(s => parseInt(s.id || s, 10)),
          diagnosis: this.regularDiagnosis.id,
          treatment: this.regularTreatment.id,
          price: this.regularPrice,
          detail: this.regularDetail,
          status: 'Planning',
          aiGenerated: false,
          _showDetails: Boolean(this.regularDetail)
        });
      });
      return items;
    },
    
    clearRegularForm() {
      this.teethSelected = [];
      this.regularToothSurfaces = [];
      this.regularDiagnosis = null;
      this.regularTreatment = null;
      this.regularPrice = 0;
      this.regularDetail = "";
      this.$v.regularDiagnosis.$reset();
      this.$v.regularTreatment.$reset();
    },

    // Modal methods
    openTreatmentModal(mode = 'add', item = null, index = null) {
      if (mode === 'edit' && item) {
        this.enterCardEditMode(item, index);
      } else {
        this.isEditingInCard = false;
        this.editingItemId = null;
        this.clearRegularForm();
      }
      this.$refs.treatmentModal.show();
    },

    resetTreatmentModal() {
      this.exitCardEditMode();
    },

    onModalTreatmentSubmit() {
      // Always check edit mode first - this prevents accidental creation of multiple rows
      if (this.isEditingInCard) {
        this.saveModalEdit();
      } else {
        // Add mode: add item, keep modal open for bulk entry
        this.onAddRegularTreatment();

        // Show brief success feedback
        this.$refs.Dialog.showToast('success', 'เพิ่มรายการแล้ว');

        // Clear form for next entry
        this.clearRegularForm();

        // Don't close modal - let user continue adding items
        // User can close manually when done
      }
    },

    saveModalEdit() {
      this.saveCardEdit();
      this.$refs.treatmentModal.hide();
    },

    cancelModalEdit() {
      this.cancelCardEdit();
      this.$refs.treatmentModal.hide();
    },
    
    openRegularTreatDiagModal() {
      this.$refs.regularTreatDiagModal?.show();
    },
    
    openRegularTreatModal() {
      this.$refs.regularTreatModal?.show();
    },
    
    
    // Favorite toggle methods (from original)
    favDxToggle(data) {
      this.isLoading = true;
      if (data.favorite == false) {
        this.$store
          .dispatch("moduleAppointment/updateTreatDiagFevorite", {
            event: data,
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else if (data.favorite == true) {
        this.$store
          .dispatch("moduleAppointment/deleteTreatDiagFevorite", {
            event: data,
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    
    favTxToggle(data) {
      this.isLoading = true;
      if (data.favorite == false) {
        this.$store
          .dispatch("moduleAppointment/updateTreatFevorite", {
            event: data,
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else if (data.favorite == true) {
        this.$store
          .dispatch("moduleAppointment/deleteTreatFevorite", {
            event: data,
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    
    // Utility methods (from original)
    customDxLabel(libs) {
      return `${libs.code ? "[" + libs.code + "]-" : ""}${libs.nameEn}`;
    },
    
    customTxDrugsLabel(option) {
      if (option.nameTh == option.nameEn) {
        return `${option.code ? "[" + option.code + "]-" : ""}${option.nameTh}`;
      } else {
        return `${option.code ? "[" + option.code + "]-" : ""}${option.nameTh}${
          option.nameEn ? " / " + option.nameEn : ""
        }`;
      }
    },

    // Navigation lock methods (mobile only to prevent accidental touches)
    enableNavigationLock() {
      // Only apply navigation lock on mobile devices
      if (!this.isMobileDevice) {
        return;
      }

      if (this.isNavigationLocked) return;

      this.isNavigationLocked = true;

      // Create handler function to prevent navigation/refresh
      this.preventNavigationHandler = (e) => {
        e.preventDefault();
        e.returnValue = 'คุณมีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก คุณแน่ใจหรือไม่ว่าต้องการออกจากหน้านี้?';
        return e.returnValue;
      };

      // Add beforeunload event listener to prevent refresh and navigation
      window.addEventListener('beforeunload', this.preventNavigationHandler);
    },

    disableNavigationLock() {
      // Skip if not mobile or not locked
      if (!this.isMobileDevice || !this.isNavigationLocked) return;

      this.isNavigationLocked = false;

      // Remove event listener
      if (this.preventNavigationHandler) {
        window.removeEventListener('beforeunload', this.preventNavigationHandler);
        this.preventNavigationHandler = null;
      }

    }
  }
};
</script>

<style scoped>
.enhanced-treatment-plan-modal {
  max-width: 95vw;
}

/* Compact Horizontal Stepper */
.horizontal-stepper {
  padding: 0.6rem 1rem;
  background: #f8f9fa;
  border-radius: 0.5rem;
}

.stepper-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.stepper-item {
  display: flex;
  align-items: center;
}

/* Inline layout: dot + label side by side */
.stepper-step-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 2;
}

.stepper-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.75rem;
  transition: all 0.3s ease;
  border: 2px solid #dee2e6;
  background: white;
  color: #6c757d;
  flex-shrink: 0;
}

/* Pending state */
.dot-pending {
  border-color: #dee2e6;
  background: white;
  color: #6c757d;
}

/* Active state */
.dot-active {
  border-color: #007bff;
  background: #007bff;
  color: white;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  transform: scale(1.05);
}

/* Completed state */
.dot-completed {
  border-color: #28a745;
  background: #28a745;
  color: white;
}

.dot-completed i {
  font-size: 0.7rem;
}

/* Connecting Line */
.stepper-line {
  width: 50px;
  height: 2px;
  background: #dee2e6;
  margin: 0 10px;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.stepper-line.line-completed {
  background: #28a745;
}

/* Step Labels (now inline) */
.stepper-label {
  color: #6c757d;
  transition: all 0.3s ease;
  font-size: 0.85rem;
  white-space: nowrap;
}

.stepper-label.label-active {
  color: #007bff;
  font-weight: 600;
}

.stepper-label.label-completed {
  color: #28a745;
  font-weight: 500;
}

.stepper-label i {
  font-size: 0.75rem;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .horizontal-stepper {
    padding: 0.5rem 0.75rem;
  }

  .stepper-line {
    width: 30px;
    margin: 0 6px;
  }

  .stepper-dot {
    width: 22px;
    height: 22px;
    font-size: 0.7rem;
  }

  .stepper-step-inline {
    gap: 6px;
  }

  .stepper-label {
    font-size: 0.75rem;
  }

  .stepper-label i {
    font-size: 0.7rem;
  }
}

.step-content {
  min-height: 400px;
}

.step-actions {
  border-top: 1px solid #dee2e6;
  padding-top: 1rem;
}

.drag-handle {
  cursor: grab;
  padding-right: 5px;
  color: #888;
}

.drag-handle:active {
  cursor: grabbing;
}

.treatment-plan-popover {
  min-width: 320px !important;
  font-size: 0.8rem;
}

/* Ultra-Compact Treatment Summary */
.treatment-summary-compact {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}

.treatment-summary-compact .card-body {
  padding: 0.5rem 0.75rem;
}

.treatment-pills {
  gap: 0.4rem;
  align-items: flex-start;
}

.treatment-badge {
  display: inline-block;
  background: #e7f3ff;
  color: #0056b3;
  padding: 0.15rem 0.45rem;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  border: 1px solid #b3d9ff;
  transition: all 0.2s ease;
}

.treatment-badge:hover {
  background: #d0e9ff;
  border-color: #80bfff;
}


</style>